// Surveillances module UI — self-contained, mounted by app-v2.jsx.
// Helpers (el, fetchJson, apiUrl, cleanText, state) are injected via bindCtx() so this
// file has no circular dependency on app-v2.jsx. Each render function returns a DOM node
// and kicks off its own async data load (fire-and-forget), matching the app's pattern.

let el, fetchJson, apiUrl, cleanText, state;

function bindCtx(ctx) {
  ({ el, fetchJson, apiUrl, cleanText, state } = ctx);
}

function isSurveillanceAdmin() {
  const u = state?.session || {};
  return Boolean(u.is_admin || u.isAdmin || u.role === "admin");
}

// Single nav item injected into app-v2.jsx's NAV array. Clicking "Surveillances"
// goes straight to the dashboard; "New surveillance" lives on the dashboard, and
// Inspectors & Companies now live under Settings → Inspectors & Companies.
export const SURVEILLANCE_NAV = { key: "surveillances", label: "Surveillances" };

export function isSurveillanceRoute(key) {
  return key === "surveillances" || key.startsWith("surveillances/") || key.startsWith("surveillances");
}

// ---- Small presentational helpers ------------------------------------------

const STATUS_COLORS = {
  "Not Started": "bg-gray-100 text-gray-700",
  Scheduled: "bg-blue-100 text-blue-700",
  "In Progress": "bg-amber-100 text-amber-800",
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-gray-200 text-gray-500 line-through",
};

function pill(text, cls) {
  return el("span", {
    class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls || "bg-gray-100 text-gray-700"}`,
    text: text || "—",
  });
}

function statusPill(status) {
  return pill(status, STATUS_COLORS[status] || "bg-gray-100 text-gray-700");
}

function progressBar(pct, { overdue = false } = {}) {
  const p = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
  // Not started = grey, in progress = blue, complete = green. Red only when overdue and incomplete.
  const color =
    p >= 100 ? "bg-green-500" :
    p > 0 ? "bg-blue-500" :
    overdue ? "bg-red-500" :
    "bg-gray-300";
  return el("div", { class: "w-full bg-gray-100 rounded-full h-2 overflow-hidden shrink-0", title: `${p}%` }, [
    el("div", { class: `${color} h-2 rounded-full`, style: `width:${p}%` }),
  ]);
}

/** Captions on top, progress bar pinned to the bottom so Physical/Records/AIR bars line up. */
function progressColumnStack(captionNodes, pct) {
  return el("div", { class: "flex flex-col justify-end gap-1 w-full min-h-[2.85rem]" }, [
    el("div", { class: "space-y-0.5" }, captionNodes.filter(Boolean)),
    progressBar(pct),
  ]);
}

function card(title, valueNode, sub) {
  return el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 shadow-sm" }, [
    el("div", { class: "text-xs uppercase tracking-wide text-gray-500", text: title }),
    typeof valueNode === "string"
      ? el("div", { class: "mt-1 text-2xl font-bold text-gray-900", text: valueNode })
      : valueNode,
    sub ? el("div", { class: "mt-1 text-xs text-gray-500", text: sub }) : null,
  ]);
}

function pageShell(title, subtitle, actions) {
  return el("div", { class: "flex items-start justify-between mb-4" }, [
    el("div", {}, [
      el("h1", { class: "text-xl font-bold text-gray-900", text: title }),
      subtitle ? el("p", { class: "text-sm text-gray-500 mt-0.5", text: subtitle }) : null,
    ]),
    actions ? el("div", { class: "flex gap-2" }, actions) : null,
  ]);
}

function surveillanceModalShell({ title, subtitle = "", bodyNode, footerNode }) {
  const overlay = el("div", {
    class: "fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4",
    role: "dialog",
    "aria-modal": "true",
  });
  const panel = el("div", { class: "bg-white rounded-xl shadow-2xl w-[95vw] max-w-[95vw] max-h-[92vh] flex flex-col" }, [
    el("div", { class: "px-5 py-4 border-b border-gray-200" }, [
      el("div", { class: "text-lg font-semibold text-gray-900", text: title }),
      subtitle ? el("div", { class: "text-xs text-gray-500 mt-0.5", text: subtitle }) : null,
    ]),
    el("div", { class: "p-4 overflow-auto flex-1" }, [bodyNode]),
    footerNode ? el("div", { class: "px-5 py-3 border-t border-gray-200" }, [footerNode]) : null,
  ]);
  overlay.appendChild(panel);
  const close = () => overlay.remove();
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  document.body.appendChild(overlay);
  return { close, node: overlay };
}

function loadingNode() {
  return el("div", { class: "text-sm text-gray-500 p-4", text: "Loading…" });
}

function errorNode(e) {
  return el("div", { class: "text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3", text: cleanText(e?.message) || String(e) });
}

function go(hash) {
  window.location.hash = hash;
}

function startOfUtcDay(input) {
  const dt = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(dt.getTime())) return NaN;
  return Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
}

/** True only after the expected calendar day has fully ended (not on the due date itself). */
function isDatePast(dateStr) {
  if (!dateStr) return false;
  const due = startOfUtcDay(dateStr);
  if (Number.isNaN(due)) return false;
  return due < startOfUtcDay(new Date());
}

function isOverdue(dateStr, status) {
  if (!dateStr) return false;
  if (status === "Completed" || status === "Cancelled") return false;
  return isDatePast(dateStr);
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return cleanText(d);
  return dt.toISOString().slice(0, 10);
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtDateDdMmmYy(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return cleanText(d);
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  const mmm = MONTH_ABBR[dt.getUTCMonth()] || "—";
  const yy = String(dt.getUTCFullYear()).slice(-2);
  return `${dd}-${mmm}-${yy}`;
}

function inspectionPerformed(row) {
  return row?.physicalStatus === "Performed" || Boolean(row?.actualInspectionDate);
}

function inspectionDateText(row) {
  const actual = row?.actualInspectionDate;
  const expected = row?.expectedInspectionDate;
  const d = actual || expected;
  return d ? fmtDateDdMmmYy(d) : "—";
}

function inspectionDateAlert(row) {
  if (inspectionPerformed(row)) return false;
  const expected = row?.expectedInspectionDate;
  if (!expected) return false;
  return isDatePast(expected);
}

function displayOverallStatus(row) {
  const status = row?.overallStatus;
  const airPct = Math.round(Number(row?.airReportPct) || 0);
  // AIR report must be 100% before an aircraft can show Completed.
  if (status === "Completed" && airPct < 100) return "In Progress";
  if (status === "Scheduled" && inspectionPerformed(row)) return "In Progress";
  if (
    status === "Completed" ||
    status === "In Progress" ||
    status === "Scheduled" ||
    status === "Cancelled"
  ) {
    return status;
  }
  // Records/physical done but AIR still open → In Progress.
  const recPct = Math.round(Number(row?.recordsPct) || 0);
  const phyPct = Math.round(Number(row?.physicalPct) || 0);
  if ((recPct >= 100 || phyPct >= 100 || airPct > 0) && airPct < 100) return "In Progress";
  return status;
}

/** Operator surveillance status from its aircraft rows (matches API roll-up). */
function deriveSurveillanceStatus(aircraftRows) {
  const rows = aircraftRows || [];
  if (!rows.length) return "Not Started";
  const list = rows.map((a) => displayOverallStatus(a) || a.overallStatus || "Not Started");
  if (list.every((s) => s === "Completed")) return "Completed";
  if (list.every((s) => s === "Cancelled")) return "Cancelled";
  if (list.some((s) => s === "In Progress" || s === "Completed")) return "In Progress";
  if (list.some((s) => s === "Scheduled")) return "Scheduled";
  if (rows.some((row) => Number(row?.recordsPct) > 0 || Number(row?.physicalPct) > 0 || Number(row?.airReportPct) > 0)) {
    return "In Progress";
  }
  return "Not Started";
}

// A lightweight SVG donut for status distribution (no chart library).
function donut(distribution) {
  const entries = Object.entries(distribution || {}).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const palette = {
    "Not Started": "#9ca3af", Scheduled: "#3b82f6", "In Progress": "#f59e0b", Completed: "#22c55e", Cancelled: "#d1d5db",
  };
  if (!total) return el("div", { class: "text-sm text-gray-400", text: "No aircraft yet" });
  const r = 60, c = 2 * Math.PI * r;
  let offset = 0;
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 160 160");
  svg.setAttribute("class", "w-40 h-40");
  for (const [k, v] of entries) {
    const frac = v / total;
    const circ = document.createElementNS(ns, "circle");
    circ.setAttribute("cx", "80"); circ.setAttribute("cy", "80"); circ.setAttribute("r", String(r));
    circ.setAttribute("fill", "none");
    circ.setAttribute("stroke", palette[k] || "#9ca3af");
    circ.setAttribute("stroke-width", "22");
    circ.setAttribute("stroke-dasharray", `${frac * c} ${c}`);
    circ.setAttribute("stroke-dashoffset", String(-offset * c));
    circ.setAttribute("transform", "rotate(-90 80 80)");
    svg.appendChild(circ);
    offset += frac;
  }
  const legend = el("div", { class: "space-y-1" },
    entries.map(([k, v]) => el("div", { class: "flex items-center gap-2 text-xs" }, [
      el("span", { class: "inline-block w-3 h-3 rounded-sm", style: `background:${palette[k] || "#9ca3af"}` }),
      el("span", { class: "text-gray-700", text: `${k} (${v})` }),
    ]))
  );
  return el("div", { class: "flex items-center gap-4" }, [svg, legend]);
}

// ---- Data loaders ----------------------------------------------------------

function api(path, opts) { return fetchJson(apiUrl(path), opts); }

function jsonBody(method, obj) {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(obj || {}) };
}

async function uploadBinary(path, file, query) {
  const qs = new URLSearchParams(query || {}).toString();
  return fetchJson(apiUrl(`${path}${qs ? `?${qs}` : ""}`), {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
}

const ALLOWED_PHOTO_EXT = new Set(["jpg", "jpeg", "png", "webp", "gif"]);

function photoFileAllowed(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (ALLOWED_PHOTO_EXT.has(ext)) return true;
  const mime = (file.type || "").toLowerCase();
  return mime === "image/jpeg" || mime === "image/png" || mime === "image/webp" || mime === "image/gif";
}

function uploadBinaryWithProgress(path, file, query, onProgress) {
  const qs = new URLSearchParams(query || {}).toString();
  const url = apiUrl(`${path}${qs ? `?${qs}` : ""}`);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.upload.addEventListener("progress", (ev) => {
      if (ev.lengthComputable && typeof onProgress === "function") onProgress(ev.loaded / ev.total);
    });
    xhr.addEventListener("load", () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText || "{}"); } catch { /* */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.error || `HTTP ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.send(file);
  });
}

async function uploadFolderPhotos(saId, folder, subfolder, files, onProgress) {
  const allowed = [];
  const rejected = [];
  for (const f of files) {
    if (photoFileAllowed(f)) allowed.push(f);
    else rejected.push(f.name);
  }
  const summary = { ok: 0, fail: 0, skipped: rejected, failedNames: [] };
  if (rejected.length) {
    const sample = rejected.slice(0, 8).join(", ");
    alert(`Skipped unsupported format (${rejected.length}): ${sample}${rejected.length > 8 ? "…" : ""}\n\nUse JPG, PNG, WEBP, or GIF.`);
  }
  if (!allowed.length) return summary;

  let completed = 0;
  const total = allowed.length;
  const report = (fileFrac = 0, currentName = "") => {
    if (typeof onProgress === "function") onProgress((completed + fileFrac) / total, completed, total, currentName);
  };

  await Promise.allSettled(
    allowed.map((file) =>
      uploadBinaryWithProgress(
        `/api/surveillances/aircraft/${saId}/photos`,
        file,
        { folder, subfolder: subfolder || "", filename: file.name, mime: file.type },
        (frac) => report(frac, file.name),
      )
        .then(() => { completed++; report(0); summary.ok++; })
        .catch((e) => {
          completed++;
          report(0);
          summary.fail++;
          summary.failedNames.push(`${file.name}: ${cleanText(e?.message)}`);
        }),
    ),
  );
  return summary;
}

function photoThumbUrl(p) {
  const v = p.isWatermarked ? "w" : "o";
  return apiUrl(`/api/surveillances/photos/${p.id}/thumb?v=${v}-${p.id}`);
}

// Operators + MSNs are derived from the already-loaded fleet (state.aircraft).
function operatorsFromFleet() {
  const map = new Map();
  for (const a of state.aircraft || []) {
    const id = a.operator_id;
    if (id && !map.has(id)) map.set(id, cleanText(a.operator) || `Operator ${id}`);
  }
  return [...map.entries()].map(([id, name]) => ({ id, name })).sort((x, y) => x.name.localeCompare(y.name));
}

function aircraftForOperator(operatorId) {
  return (state.aircraft || [])
    .filter((a) => String(a.operator_id) === String(operatorId) && cleanText(a.msn))
    .map((a) => ({ id: a.id, msn: cleanText(a.msn), registration: cleanText(a.registration) }))
    .sort((x, y) => x.msn.localeCompare(y.msn));
}

// ---- Inspector pickers (grouped Company | Inspector) -----------------------

function inspectorName(i) {
  return `${cleanText(i.firstName)} ${cleanText(i.lastName) || ""}`.trim() || `Inspector ${i.id}`;
}

function groupInspectorsByCompany(inspectors) {
  const groups = new Map();
  for (const i of inspectors || []) {
    const co = cleanText(i.companyName) || "— No company —";
    if (!groups.has(co)) groups.set(co, []);
    groups.get(co).push(i);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

// One combobox showing companies as group headers and inspectors as options.
function inspectorSelect(inspectors, selectedId, opts = {}) {
  const sel = el("select", {
    class: `${opts.class || "border rounded px-2 py-1 text-xs"} ${opts.disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`,
    disabled: Boolean(opts.disabled),
    title: opts.disabled ? "Inspector assignments are admin-only" : undefined,
  });
  sel.appendChild(el("option", { value: "", text: opts.placeholder || "—", selected: !selectedId }));
  for (const [co, list] of groupInspectorsByCompany(inspectors)) {
    const og = document.createElement("optgroup");
    og.label = co;
    for (const i of list) og.appendChild(el("option", { value: String(i.id), text: inspectorName(i), selected: String(i.id) === String(selectedId) }));
    sel.appendChild(og);
  }
  if (opts.onSave) sel.addEventListener("change", () => opts.onSave(sel.value || null));
  return sel;
}

// Tiny "Saved" flash shown next to inline-edited fields, no re-render needed.
function savedFlash() {
  return el("span", { class: "text-[10px] text-gray-300 transition-colors ml-1", text: "•" });
}
function flashSaved(node, ok = true) {
  if (!node) return;
  node.textContent = ok ? "✓ saved" : "✕ failed";
  node.className = `text-[10px] ml-1 ${ok ? "text-green-600" : "text-red-600"}`;
  setTimeout(() => { node.textContent = "•"; node.className = "text-[10px] text-gray-300 ml-1"; }, 1500);
}

// PUT one or more fields on a surveillance_aircraft row.
function saveAircraftPatch(saId, patch) {
  return api(`/api/surveillances/aircraft/${saId}`, jsonBody("PUT", patch));
}

// ---- Router ----------------------------------------------------------------

export function renderSurveillanceRoute(ctx, key, params) {
  bindCtx(ctx);
  const host = el("div", { class: "p-4 w-[90%] max-w-[90vw] mx-auto" }, [loadingNode()]);

  const detail = /^surveillances\/(\d+)$/.exec(key);
  const aircraftDetail = /^surveillances\/(\d+)\/aircraft\/(\d+)$/.exec(key);
  const edit = /^surveillances\/(\d+)\/edit$/.exec(key);

  // Everything is inline on the dashboard now: the old per-surveillance and
  // per-aircraft pages and the full-page editor redirect to the dashboard,
  // auto-expanding the relevant surveillance.
  if (key === "surveillances") initDashboard(host, params);
  else if (key === "surveillances/new") initEditor(host);
  else if (edit) go(`#/surveillances?expand=${edit[1]}`);
  else if (key === "surveillances/settings") go("#/settings?tab=inspectors"); // moved into Settings
  else if (aircraftDetail) go(`#/surveillances?expand=${aircraftDetail[1]}`);
  else if (detail) go(`#/surveillances?expand=${detail[1]}`);
  else host.replaceChildren(errorNode({ message: "Unknown surveillance route" }));

  return host;
}

// Mounted by app-v2.jsx's Settings page as the "Inspectors & Companies" tab.
export function renderSurveillanceSettingsPanel(ctx) {
  bindCtx(ctx);
  const host = el("div", {}, [loadingNode()]);
  initSettings(host, { embedded: true });
  return host;
}

/** Full interactive surveillance table embedded on Operator progress (same actions as Surveillances page). */
export function renderOperatorSurveillanceEmbed(ctx, { operatorId, operatorName, sectionHeading, openOnly = true }) {
  bindCtx(ctx);
  const host = el("section", { class: "mb-6" });
  const contentMount = el("div", {});
  host.appendChild(sectionHeading("I · Surveillances", {
    subtitle: `${cleanText(operatorName) || "This operator"} — edit inspectors, PO, dates, records & photos inline`,
  }));
  host.appendChild(contentMount);
  contentMount.appendChild(loadingNode());

  (async () => {
    if (!operatorId) {
      contentMount.replaceChildren(el("p", { class: "text-sm text-gray-500", text: "Select an operator to manage surveillances." }));
      return;
    }
    try {
      const [list, inspectorsRes] = await Promise.all([
        api(`/api/surveillances?operatorId=${encodeURIComponent(operatorId)}`),
        api("/api/surveillances/inspectors"),
      ]);
      let rows = list.surveillances || [];
      if (openOnly) {
        rows = rows.filter((s) => {
          const st = String(s.status || "").toLowerCase();
          return st !== "completed" && st !== "cancelled";
        });
      }
      const inspectors = inspectorsRes.inspectors || [];
      const toolbar = el("div", { class: "flex flex-wrap items-center gap-2 mb-3" }, [
        el("button", {
          class: "px-3 py-1.5 text-xs font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
          text: "+ New surveillance",
          onclick: () => go("#/surveillances/new"),
        }),
        el("a", {
          href: `#/surveillances?operatorId=${encodeURIComponent(operatorId)}`,
          class: "text-xs text-dae hover:underline",
          text: "Open Surveillances page ↗",
        }),
      ]);
      if (!rows.length) {
        contentMount.replaceChildren(
          toolbar,
          el("p", { class: "text-sm text-gray-500", text: "No open surveillances for this operator." }),
        );
        return;
      }
      contentMount.replaceChildren(
        toolbar,
        surveillanceTable(rows, inspectors, { autoExpandAll: true, hideOperatorColumn: true }),
      );
    } catch (e) {
      contentMount.replaceChildren(errorNode(e));
    }
  })();

  return host;
}

// ---- Dashboard + list ------------------------------------------------------

async function initDashboard(host, params) {
  try {
    const qs = new URLSearchParams();
    for (const k of ["quarter", "year", "operatorId"]) if (params.get(k)) qs.set(k, params.get(k));
    const [dash, list, inspectorsRes] = await Promise.all([
      api("/api/surveillances/dashboard"),
      api(`/api/surveillances${qs.toString() ? `?${qs}` : ""}`),
      api("/api/surveillances/inspectors"),
    ]);
    const inspectors = inspectorsRes.inspectors || [];
    const m = dash.metrics || {};
    const fleetCompletion = Math.round(((m.avgRecordsPct || 0) + (m.avgPhysicalPct || 0)) / 2);
    const cards = el("div", { class: "grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" }, [
      card("Operators", String(m.operatorsInProgress ?? 0), "in progress"),
      card("Aircraft tracked", String(m.totalAircraft ?? 0)),
      card("Records progress", `${m.avgRecordsPct ?? 0}%`, "fleet average"),
      card("Physical progress", `${m.avgPhysicalPct ?? 0}%`, "fleet average"),
      card("Overdue", String(m.overdue ?? 0), m.overdue ? "past finalization date" : "all on schedule"),
    ]);

    const charts = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-5" }, [
      el("div", { class: "bg-white rounded-xl border border-gray-200 p-4" }, [
        el("div", { class: "text-sm font-semibold text-gray-800 mb-2", text: "Status distribution" }),
        donut(m.statusDistribution),
      ]),
      el("div", { class: "bg-white rounded-xl border border-gray-200 p-4" }, [
        el("div", { class: "text-sm font-semibold text-gray-800", text: "Fleet progress" }),
        el("div", { class: "text-xs text-gray-500 mt-0.5 mb-3", text: `Overall completion = 50% records + 50% physical (${fleetCompletion}% today)` }),
        el("div", { class: "space-y-3" }, [
          el("div", {}, [el("div", { class: "flex justify-between text-xs mb-1" }, [el("span", { text: "Records" }), el("span", { text: `${m.avgRecordsPct || 0}%` })]), progressBar(m.avgRecordsPct)]),
          el("div", {}, [el("div", { class: "flex justify-between text-xs mb-1" }, [el("span", { text: "Physical" }), el("span", { text: `${m.avgPhysicalPct || 0}%` })]), progressBar(m.avgPhysicalPct)]),
        ]),
      ]),
    ]);

    host.replaceChildren(
      pageShell("Surveillances", "Quarterly records reviews & physical inspections", [
        isSurveillanceAdmin()
          ? el("button", { class: "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90", text: "+ New surveillance", onclick: () => go("#/surveillances/new") })
          : null,
      ]),
      cards,
      charts,
      surveillanceTable(list.surveillances || [], inspectors, params.get("expand")),
    );
  } catch (e) {
    host.replaceChildren(errorNode(e));
  }
}

function parseSurveillanceTableOpts(third) {
  if (third && typeof third === "object") return third;
  return { expandId: third };
}

function surveillanceTable(rows, inspectors, third) {
  const { expandId, autoExpandAll = false, hideOperatorColumn = false } = parseSurveillanceTableOpts(third);
  if (!rows.length) {
    return el("div", { class: "bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 text-sm" }, [
      el("div", { text: "No surveillances yet." }),
      el("button", { class: "mt-3 px-4 py-2 text-sm text-white bg-dae rounded-lg", text: "Create the first one", onclick: () => go("#/surveillances/new") }),
    ]);
  }

  const sortState = { key: "quarter", dir: "desc" };
  const headDefs = [
    { label: "", key: null },
    { label: "Quarter", key: "quarter" },
    ...(hideOperatorColumn ? [] : [{ label: "Operator", key: "operator" }]),
    { label: "Aircraft", key: "aircraftCount" },
    { label: "Records", key: "avgRecordsPct" },
    { label: "Physical", key: "avgPhysicalPct" },
    { label: "Finalization", key: "expectedFinalizationDate" },
    { label: "Status", key: "status" },
  ];

  const sortValue = (s, key) => {
    if (key === "quarter") return `${s.year || 0}-${s.quarter || ""}`;
    if (key === "operator") return cleanText(s.operator) || "";
    if (key === "aircraftCount") return Number(s.aircraftCount) || 0;
    if (key === "avgRecordsPct") return Number(s.avgRecordsPct) || 0;
    if (key === "avgPhysicalPct") return Number(s.avgPhysicalPct) || 0;
    if (key === "expectedFinalizationDate") return s.expectedFinalizationDate || "";
    if (key === "status") return cleanText(s.status) || "";
    return "";
  };

  const thead = el("thead", { class: "bg-gray-50 text-gray-600" });
  const tbody = el("tbody", {});
  const table = el("table", { class: "surv-fit-table w-full text-sm" }, [thead, tbody]);
  const wrap = el("div", { class: "surv-fit-wrap bg-white rounded-xl border border-gray-200" }, [table]);

  const paintHead = () => {
    thead.replaceChildren(
      el(
        "tr",
        {},
        headDefs.map((h) =>
          sortableTh(h.label, h.key, sortState, (key) => {
            toggleSortState(sortState, key);
            paintHead();
            paintBody();
          })
        )
      )
    );
  };

  const paintBody = () => {
    tbody.replaceChildren();
    const sorted = [...rows].sort((a, b) => {
      const cmp = compareSortValues(sortValue(a, sortState.key), sortValue(b, sortState.key));
      return sortState.dir === "asc" ? cmp : -cmp;
    });
    const autoOpens = [];
    for (const s of sorted) {
      const overdue = isOverdue(s.expectedFinalizationDate, s.status);
      const caret = el("span", { class: "inline-block text-gray-400 transition-transform", text: "▸" });
      const detailCell = el("td", { class: "p-0", colspan: String(headDefs.length) });
      const detailRow = el("tr", { class: "hidden bg-gray-50/70 border-t border-gray-100" }, [detailCell]);

      const countCell = el("td", { class: "px-3 py-2", text: String(s.aircraftCount) });
      const recCell = el("td", { class: "px-3 py-2 w-32" }, [progressBar(s.avgRecordsPct)]);
      const phyCell = el("td", { class: "px-3 py-2 w-32" }, [progressBar(s.avgPhysicalPct)]);
      const statusCell = el("td", { class: "px-3 py-2" }, [statusPill(s.status)]);
      const parentUpdate = (aircraftRows) => {
        const n = aircraftRows.length || 1;
        const avgR = Math.round(aircraftRows.reduce((x, a) => x + Number(a.recordsPct || 0), 0) / n);
        const avgP = Math.round(aircraftRows.reduce((x, a) => x + Number(a.physicalPct || 0), 0) / n);
        countCell.textContent = String(aircraftRows.length);
        recCell.replaceChildren(progressBar(avgR));
        phyCell.replaceChildren(progressBar(avgP));
        statusCell.replaceChildren(statusPill(deriveSurveillanceStatus(aircraftRows)));
      };

      let loaded = false;
      const open = async () => {
        detailRow.classList.remove("hidden");
        caret.style.transform = "rotate(90deg)";
        if (!loaded) {
          loaded = true;
          await loadAircraftPanel(detailCell, s, inspectors, parentUpdate);
        }
      };
      const headRow = el(
        "tr",
        {
          class: "border-t border-gray-100 hover:bg-gray-50 cursor-pointer",
          onclick: () => {
            if (detailRow.classList.contains("hidden")) open();
            else {
              detailRow.classList.add("hidden");
              caret.style.transform = "";
            }
          },
        },
        [
          el("td", { class: "px-3 py-2 w-6" }, [caret]),
          el("td", { class: "px-3 py-2 font-medium", text: `${s.quarter} ${s.year}` }),
          ...(hideOperatorColumn ? [] : [el("td", { class: "px-3 py-2", text: cleanText(s.operator) || "—" })]),
          countCell,
          recCell,
          phyCell,
          el("td", {
            class: `px-3 py-2 ${overdue ? "text-red-600 font-semibold" : ""}`,
            text: fmtDate(s.expectedFinalizationDate),
          }),
          statusCell,
        ]
      );
      tbody.appendChild(headRow);
      tbody.appendChild(detailRow);
      if (expandId && String(expandId) === String(s.id)) open();
      else if (autoExpandAll) autoOpens.push(open);
    }
    if (autoOpens.length) Promise.all(autoOpens.map((fn) => fn())).catch(() => {});
  };

  paintHead();
  paintBody();
  return wrap;
}

// Fetch + render the inline aircraft panel into a surveillance row's detail cell.
// Re-callable to fully rebuild after an aircraft is added or removed.
async function loadAircraftPanel(detailCell, s, inspectors, parentUpdate) {
  detailCell.replaceChildren(el("div", { class: "p-3" }, [loadingNode()]));
  try {
    const res = await api(`/api/surveillances/${s.id}`);
    const rows = res.aircraft || [];
    detailCell.replaceChildren(buildAircraftPanel(s, rows, inspectors, parentUpdate, () => loadAircraftPanel(detailCell, s, inspectors, parentUpdate)));
    parentUpdate?.(rows);
  } catch (e) {
    detailCell.replaceChildren(el("div", { class: "p-3" }, [errorNode(e)]));
  }
}

function inspectorLabel(insMap, id) {
  if (!id) return "—";
  const i = insMap.get(String(id));
  if (!i) return "—";
  const co = cleanText(i.companyName);
  return co ? `${inspectorName(i)} · ${co}` : inspectorName(i);
}

function greenLight(on, title) {
  return el("span", {
    class: `inline-block w-3 h-3 rounded-full align-middle ${on ? "bg-green-500" : "bg-gray-300"}`,
    title: title || (on ? "Performed" : "Not performed"),
  });
}

/** External link chain icon (shown when a records URL is present). */
function linkChainIcon({ title = "Open records link" } = {}) {
  // `el()` uses createElement (HTML namespace), so nested SVG paths rendered blank.
  // A text glyph is namespace-independent and remains visible in every browser.
  return el("span", {
    class: "inline-block text-base leading-none text-dae",
    text: "🔗",
    title,
    "aria-hidden": "true",
  });
}

function normalizeExternalUrl(raw) {
  const u = cleanText(raw);
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (/^\/\//.test(u)) return `https:${u}`;
  return `https://${u}`;
}

function recordsLinkAnchor(url, { stopPropagation = true } = {}) {
  const href = normalizeExternalUrl(url);
  if (!href) return el("span", { class: "text-gray-300", text: "—" });
  return el(
    "a",
    {
      href,
      target: "_blank",
      rel: "noopener noreferrer",
      class: "inline-flex items-center justify-center p-1 rounded hover:bg-dae/10",
      title: href,
      onclick: (e) => {
        if (stopPropagation) e.stopPropagation();
      },
    },
    [linkChainIcon({ title: href })]
  );
}

function compareSortValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null || a === "") return 1;
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(b) - Number(a);
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

function sortableTh(label, key, sortState, onSort, extraClass = "") {
  if (!label) {
    return el("th", { class: `text-left font-medium px-1.5 py-2 ${extraClass}` });
  }
  const active = sortState?.key === key;
  const arrow = active ? (sortState.dir === "asc" ? " ▲" : " ▼") : "";
  return el("th", {
    class: `text-left font-medium px-1.5 py-2 cursor-pointer select-none hover:text-gray-900 leading-tight ${extraClass}`,
    title: `Sort by ${label}`,
    onclick: (e) => {
      e.stopPropagation();
      onSort?.(key);
    },
    text: `${label}${arrow}`,
  });
}

function toggleSortState(sortState, key) {
  if (sortState.key === key) {
    sortState.dir = sortState.dir === "asc" ? "desc" : "asc";
  } else {
    sortState.key = key;
    sortState.dir = "asc";
  }
}

/** Records upload state from progress % — drives the red / blue / green light. */
const RECORDS_UPLOAD_STATES = [
  { v: "not_done", t: "Not done", pct: 0, color: "bg-red-500", title: "Not done" },
  { v: "in_progress", t: "In progress", pct: 50, color: "bg-blue-500", title: "In progress" },
  { v: "uploaded", t: "Uploaded", pct: 100, color: "bg-green-500", title: "Uploaded" },
];

function recordsUploadStateFromPct(pct) {
  const p = Math.round(Number(pct) || 0);
  if (p >= 100) return "uploaded";
  if (p > 0) return "in_progress";
  return "not_done";
}

function pctFromRecordsUploadState(state, currentPct) {
  if (state === "uploaded") return 100;
  if (state === "not_done") return 0;
  const p = Math.round(Number(currentPct) || 0);
  if (p > 0 && p < 100) return nearestProgressStep(p).v;
  return 50;
}

function recordsStatusLight(pctOrState) {
  const state =
    pctOrState === "not_done" || pctOrState === "in_progress" || pctOrState === "uploaded"
      ? pctOrState
      : recordsUploadStateFromPct(pctOrState);
  const meta = RECORDS_UPLOAD_STATES.find((s) => s.v === state) || RECORDS_UPLOAD_STATES[0];
  return el("span", {
    class: `inline-block w-3 h-3 rounded-full align-middle ${meta.color}`,
    title: meta.title,
  });
}

function recordsUploadStateSelect(currentPct, { className = "border rounded px-2 py-1 text-sm" } = {}) {
  const state = recordsUploadStateFromPct(currentPct);
  return el(
    "select",
    { class: className },
    RECORDS_UPLOAD_STATES.map((o) =>
      el("option", { value: o.v, text: o.t, selected: o.v === state }),
    ),
  );
}

// ---- Excel export (SpreadsheetML with page-matching colors) ----------------

function xmlEsc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function downloadBlobFile({ filename, content, contentType }) {
  const blob = new Blob([content], { type: contentType || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Progress fill colors matching the UI progress bars (green / blue / gray). */
function progressStyleId(pct) {
  const p = Math.round(Number(pct) || 0);
  if (p >= 100) return "pct100";
  if (p > 0) return "pctProg";
  return "pct0";
}

function statusStyleId(status) {
  const s = cleanText(status) || "";
  if (s === "Completed") return "stDone";
  if (s === "In Progress") return "stProg";
  if (s === "Scheduled") return "stSched";
  if (s === "Cancelled") return "stCancel";
  return "stIdle";
}

function recordsLightStyleId(pct) {
  const state = recordsUploadStateFromPct(pct);
  if (state === "uploaded") return "recUp";
  if (state === "in_progress") return "recProg";
  return "recNo";
}

function buildSurveillanceAircraftXls({ title, surveillance, rows, inspectors }) {
  const insMap = new Map((inspectors || []).map((i) => [String(i.id), i]));
  const headers = [
    "MSN",
    "Registration",
    "Inspector records",
    "Inspector physical",
    "LAUNCHED",
    "PO records",
    "PO physical",
    "Location",
    "Inspection date",
    "Physical %",
    "Inspected",
    "Photos",
    "Records %",
    "Records status",
    "Records link",
    "AIR %",
    "Status",
    "Comments",
  ];
  const colWidths = [70, 80, 140, 140, 70, 80, 80, 120, 90, 70, 70, 70, 70, 90, 180, 60, 90, 200];

  const body = (rows || []).map((row) => {
    const recPct = Math.round(Number(row.recordsPct) || 0);
    const phyPct = Math.round(Number(row.physicalPct) || 0);
    const airPct = Math.round(Number(row.airReportPct) || 0);
    const status = displayOverallStatus(row);
    const recState = RECORDS_UPLOAD_STATES.find((x) => x.v === recordsUploadStateFromPct(recPct))?.t || "Not done";
    const performed = inspectionPerformed(row);
    const photosUp = Boolean(row.photosUploaded) || Number(row.photoCount || 0) > 0;
    const photoN = Number(row.photoCount || 0);
    const link = cleanText(row.airReportRecordsLink) || "";
    const cells = [
      { t: cleanText(row.msn) || "", s: "cell" },
      { t: cleanText(row.registration) || "", s: "cell" },
      { t: inspectorLabel(insMap, row.inspectorRecordsId), s: "cell" },
      { t: inspectorLabel(insMap, row.inspectorPhysicalId), s: "cell" },
      { t: row.atsAccess ? "Yes" : "No", s: row.atsAccess ? "launchYes" : "launchNo" },
      { t: cleanText(row.poRecords) || "", s: "cell" },
      { t: cleanText(row.poPhysical) || "", s: "cell" },
      { t: cleanText(row.expectedLocation) || "", s: "cell" },
      { t: inspectionDateText(row), s: inspectionDateAlert(row) ? "dateAlert" : "cell" },
      { t: `${phyPct}%`, s: progressStyleId(phyPct) },
      { t: performed ? "Yes" : "No", s: performed ? "launchYes" : "launchNo" },
      { t: photosUp ? `${photoN}` : "—", s: photosUp ? "launchYes" : "launchNo" },
      { t: `${recPct}%`, s: progressStyleId(recPct) },
      { t: recState, s: recordsLightStyleId(recPct) },
      { t: link, s: link ? "linkCell" : "cell" },
      { t: `${airPct}%`, s: progressStyleId(airPct) },
      { t: status, s: statusStyleId(status) },
      { t: cleanText(row.comments) || "", s: "cell" },
    ];
    return `<Row>${cells
      .map((c) => `<Cell ss:StyleID="${c.s}"><Data ss:Type="String">${xmlEsc(c.t)}</Data></Cell>`)
      .join("")}</Row>`;
  }).join("\n");

  const headerRow = `<Row ss:AutoFitHeight="0" ss:Height="22">${headers
    .map((h) => `<Cell ss:StyleID="hdr"><Data ss:Type="String">${xmlEsc(h)}</Data></Cell>`)
    .join("")}</Row>`;
  const cols = colWidths.map((w) => `<Column ss:AutoFitWidth="0" ss:Width="${w}"/>`).join("");
  const sheetName = xmlEsc(
    String(title || "Surveillances")
      .replace(/[\\\/\?\*\[\]:]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 31) || "Aircraft",
  );
  const totalRows = (rows || []).length + 1;
  const meta = [
    cleanText(surveillance?.operator) || "",
    `${surveillance?.quarter || ""} ${surveillance?.year || ""}`.trim(),
  ].filter(Boolean).join(" · ");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Top"/></Style>
  <Style ss:ID="hdr">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#C00000" ss:Pattern="Solid"/>
   <Alignment ss:Vertical="Center" ss:WrapText="1"/>
  </Style>
  <Style ss:ID="cell"><Alignment ss:Vertical="Top" ss:WrapText="1"/></Style>
  <Style ss:ID="pct100"><Font ss:Bold="1" ss:Color="#166534"/><Interior ss:Color="#BBF7D0" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="pctProg"><Font ss:Bold="1" ss:Color="#1E40AF"/><Interior ss:Color="#BFDBFE" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="pct0"><Font ss:Color="#4B5563"/><Interior ss:Color="#E5E7EB" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="recUp"><Font ss:Bold="1" ss:Color="#166534"/><Interior ss:Color="#86EFAC" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="recProg"><Font ss:Bold="1" ss:Color="#1E40AF"/><Interior ss:Color="#60A5FA" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="recNo"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#EF4444" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="launchYes"><Font ss:Bold="1" ss:Color="#166534"/><Interior ss:Color="#86EFAC" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="launchNo"><Font ss:Color="#6B7280"/><Interior ss:Color="#E5E7EB" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="stDone"><Font ss:Bold="1" ss:Color="#15803D"/><Interior ss:Color="#DCFCE7" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="stProg"><Font ss:Bold="1" ss:Color="#92400E"/><Interior ss:Color="#FEF3C7" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="stSched"><Font ss:Bold="1" ss:Color="#1D4ED8"/><Interior ss:Color="#DBEAFE" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="stIdle"><Font ss:Color="#374151"/><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="stCancel"><Font ss:Color="#6B7280"/><Interior ss:Color="#E5E7EB" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="dateAlert"><Font ss:Bold="1" ss:Color="#DC2626"/><Alignment ss:Vertical="Top"/></Style>
  <Style ss:ID="linkCell"><Font ss:Color="#C00000" ss:Underline="Single"/><Alignment ss:Vertical="Top" ss:WrapText="1"/></Style>
 </Styles>
 <Worksheet ss:Name="${sheetName}">
  <Table>
   ${cols}
   ${meta ? `<Row><Cell ss:StyleID="cell" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEsc(meta)}</Data></Cell></Row>` : ""}
   ${headerRow}
   ${body}
  </Table>
  <AutoFilter x:Range="R${meta ? 2 : 1}C1:R${totalRows + (meta ? 1 : 0)}C${headers.length}" xmlns="urn:schemas-microsoft-com:office:excel"/>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>${meta ? 2 : 1}</SplitHorizontal>
   <TopRowBottomPane>${meta ? 2 : 1}</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;
}

function exportSurveillanceAircraftExcel(s, rows, inspectors) {
  const op = cleanText(s.operator) || "operator";
  const q = `${s.quarter || ""}${s.year || ""}`.replace(/\s+/g, "");
  const filename = `surveillance-${op.replace(/[^\w.-]+/g, "_")}-${q || "fleet"}-aircraft.xls`;
  const xml = buildSurveillanceAircraftXls({
    title: `${s.quarter || ""} ${s.year || ""} ${op}`.trim() || "Aircraft",
    surveillance: s,
    rows,
    inspectors,
  });
  downloadBlobFile({
    filename,
    content: xml,
    contentType: "application/vnd.ms-excel;charset=utf-8",
  });
}

function buildOpenItemsMatrixXls(s, aircraft, items) {
  const headers = [
    "Checklist item",
    ...aircraft.map((a) => `${cleanText(a.msn) || "MSN"}${a.registration ? ` · ${a.registration}` : ""}`),
  ];
  const statusLabel = {
    not_yet_provided: "Open",
    provided: "Provided",
    already_in_ats: "In ATS",
    will_not_be_provided: "N/A",
  };
  const statusStyle = {
    not_yet_provided: "open",
    provided: "provided",
    already_in_ats: "ats",
    will_not_be_provided: "na",
  };
  const body = items.map((item) => {
    const itemText = `${item.atsNode || "—"} · ${item.label}`;
    const cells = aircraft.map((a) => {
      const cell = item.cells?.[String(a.saId)] || { status: "not_yet_provided", fileCount: 0 };
      const count = Number(cell.fileCount) || 0;
      const text = `${statusLabel[cell.status] || "Open"}${count ? ` · ${count} file${count === 1 ? "" : "s"}` : ""}`;
      return `<Cell ss:StyleID="${statusStyle[cell.status] || "open"}"><Data ss:Type="String">${xmlEsc(text)}</Data></Cell>`;
    }).join("");
    return `<Row><Cell ss:StyleID="item"><Data ss:Type="String">${xmlEsc(itemText)}</Data></Cell>${cells}</Row>`;
  }).join("\n");
  const headerRow = `<Row ss:Height="26">${headers.map((h) =>
    `<Cell ss:StyleID="hdr"><Data ss:Type="String">${xmlEsc(h)}</Data></Cell>`
  ).join("")}</Row>`;
  const sheetName = xmlEsc(
    `Open ${cleanText(s.operator) || "items"}`
      .replace(/[\\\/\?\*\[\]:]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 31) || "Open items",
  );
  const meta = `${cleanText(s.operator) || "Surveillance"} · ${s.quarter || ""} ${s.year || ""}`.trim();
  const lastRow = items.length + 2;
  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Center"/></Style>
  <Style ss:ID="meta"><Font ss:Bold="1" ss:Color="#374151"/><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/></Style>
  <Style ss:ID="hdr"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#C00000" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/></Style>
  <Style ss:ID="item"><Font ss:Color="#1F2937"/><Alignment ss:Vertical="Center" ss:WrapText="1"/></Style>
  <Style ss:ID="open"><Font ss:Bold="1" ss:Color="#B91C1C"/><Interior ss:Color="#FEE2E2" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="provided"><Font ss:Bold="1" ss:Color="#166534"/><Interior ss:Color="#DCFCE7" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="ats"><Font ss:Bold="1" ss:Color="#1D4ED8"/><Interior ss:Color="#DBEAFE" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="na"><Font ss:Color="#6B7280"/><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
 </Styles>
 <Worksheet ss:Name="${sheetName}">
  <Table>
   <Column ss:AutoFitWidth="0" ss:Width="360"/>
   ${aircraft.map(() => `<Column ss:AutoFitWidth="0" ss:Width="105"/>`).join("")}
   <Row><Cell ss:StyleID="meta" ss:MergeAcross="${aircraft.length}"><Data ss:Type="String">${xmlEsc(meta)}</Data></Cell></Row>
   ${headerRow}
   ${body}
  </Table>
  <AutoFilter x:Range="R2C1:R${lastRow}C${headers.length}" xmlns="urn:schemas-microsoft-com:office:excel"/>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/><FrozenNoSplit/><SplitHorizontal>2</SplitHorizontal><TopRowBottomPane>2</TopRowBottomPane>
   <SplitVertical>1</SplitVertical><LeftColumnRightPane>1</LeftColumnRightPane><ActivePane>0</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;
}

function exportOpenItemsMatrixExcel(s, aircraft, items) {
  const op = cleanText(s.operator) || "operator";
  const q = `${s.quarter || ""}${s.year || ""}`.replace(/\s+/g, "");
  downloadBlobFile({
    filename: `surveillance-${op.replace(/[^\w.-]+/g, "_")}-${q || "fleet"}-open-items-matrix.xls`,
    content: buildOpenItemsMatrixXls(s, aircraft, items),
    contentType: "application/vnd.ms-excel;charset=utf-8",
  });
}

async function openSurveillanceMatrix(s) {
  try {
    const res = await api(`/api/surveillances/${s.id}/matrix`);
    const aircraft = res.aircraft || [];
    const items = res.items || [];
    const statusMeta = {
      not_yet_provided: ["Open", "bg-red-50 text-red-700"],
      provided: ["Provided", "bg-green-50 text-green-700"],
      already_in_ats: ["In ATS", "bg-blue-50 text-blue-700"],
      will_not_be_provided: ["N/A", "bg-gray-100 text-gray-500"],
    };
    const table = el("table", { class: "min-w-max text-xs border-collapse" }, [
      el("thead", { class: "sticky top-0 z-10 bg-dae text-white" }, [
        el("tr", {}, [
          el("th", { class: "sticky left-0 z-20 bg-dae px-3 py-2 text-left min-w-[22rem]", text: "Open checklist item" }),
          ...aircraft.map((a) => el("th", {
            class: "px-3 py-2 text-center min-w-[7rem] whitespace-nowrap",
            text: `${cleanText(a.msn) || "MSN"}${a.registration ? ` · ${a.registration}` : ""}`,
          })),
        ]),
      ]),
      el("tbody", {}, items.length
        ? items.map((item) => el("tr", { class: "border-b border-gray-200" }, [
            el("td", {
              class: "sticky left-0 bg-white px-3 py-2 text-gray-800",
              text: `${item.atsNode || "—"} · ${item.label}`,
            }),
            ...aircraft.map((a) => {
              const cell = item.cells?.[String(a.saId)] || { status: "not_yet_provided", fileCount: 0 };
              const meta = statusMeta[cell.status] || statusMeta.not_yet_provided;
              return el("td", { class: "px-2 py-2 text-center" }, [
                el("span", {
                  class: `inline-flex rounded px-2 py-1 whitespace-nowrap ${meta[1]}`,
                  text: `${meta[0]}${cell.fileCount ? ` · ${cell.fileCount} file${cell.fileCount === 1 ? "" : "s"}` : ""}`,
                }),
              ]);
            }),
          ]))
        : [el("tr", {}, [
            el("td", {
              colspan: String(Math.max(1, aircraft.length + 1)),
              class: "px-4 py-8 text-center text-gray-500",
              text: aircraft.length ? "No open checklist items." : "No accessible aircraft in this surveillance.",
            }),
          ])]),
    ]);
    const excelBtn = el("button", {
      class: "px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
      text: "⬇ Excel export",
      onclick: () => exportOpenItemsMatrixExcel(s, aircraft, items),
    });
    const closeBtn = el("button", { class: "px-3 py-2 text-sm border rounded-lg", text: "Close" });
    const dialog = surveillanceModalShell({
      title: `Open records matrix — ${cleanText(s.operator) || "Surveillance"}`,
      subtitle: `${s.quarter || ""} ${s.year || ""} · checklist rows × MSN columns`,
      bodyNode: el("div", { class: "max-h-[68vh] overflow-auto border border-gray-200 rounded-lg" }, [table]),
      footerNode: el("div", { class: "flex justify-between items-center w-full" }, [
        el("div", { class: "text-xs text-gray-500", text: `${items.length} open checklist row${items.length === 1 ? "" : "s"} · ${aircraft.length} MSN${aircraft.length === 1 ? "" : "s"}` }),
        el("div", { class: "flex items-center gap-2" }, [excelBtn, closeBtn]),
      ]),
    });
    closeBtn.onclick = dialog.close;
  } catch (e) {
    alert(cleanText(e?.message) || "Could not load open items matrix");
  }
}

// The inline aircraft table for one surveillance. `rebuild` re-fetches + re-renders
// the whole panel (after add/remove); `refresh` repaints summary cells in place after
// a field edit (no collapse, no focus loss) — this is what makes edits show instantly.
function buildAircraftPanel(s, aircraftRows, inspectors, parentUpdate, rebuild) {
  const insMap = new Map(inspectors.map((i) => [String(i.id), i]));
  const wrap = el("div", { class: "p-3 space-y-2" });
  let workingRows = [...aircraftRows];
  const admin = isSurveillanceAdmin();

  // ── Header + inline "＋ Add aircraft" ──
  const attachedIds = new Set(aircraftRows.map((a) => String(a.aircraftId)));
  const available = aircraftForOperator(s.operatorId).filter((f) => !attachedIds.has(String(f.id)));
  const addPanel = el("div", { class: "hidden bg-white border border-gray-200 rounded-lg p-2 mb-2" });
  const addBtn = el("button", { class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-white", text: "＋ Add aircraft", onclick: () => addPanel.classList.toggle("hidden") });
  if (!available.length) {
    addPanel.append(el("div", { class: "text-xs text-gray-400", text: "All of this operator's MSNs are already attached." }));
  } else {
    const picks = new Set();
    const checks = [];
    const allCb = el("input", { type: "checkbox", onchange: () => { picks.clear(); if (allCb.checked) available.forEach((f) => picks.add(f.id)); checks.forEach((c) => (c.checked = allCb.checked)); } });
    const grid = el("div", { class: "grid grid-cols-2 md:grid-cols-4 gap-1 my-2" }, available.map((f) => {
      const cb = el("input", { type: "checkbox", onchange: (e) => { e.target.checked ? picks.add(f.id) : picks.delete(f.id); } });
      checks.push(cb);
      return el("label", { class: "flex items-center gap-2 text-xs" }, [cb, el("span", { text: `${f.msn}${f.registration ? ` · ${f.registration}` : ""}` })]);
    }));
    const addSelected = el("button", { class: "text-xs text-white bg-dae rounded px-3 py-1", text: "Add selected", onclick: async () => {
      if (!picks.size) return;
      try { await api(`/api/surveillances/${s.id}/aircraft`, jsonBody("POST", { aircraft: [...picks].map((id) => ({ aircraftId: id })) })); rebuild(); }
      catch (e) { alert(cleanText(e?.message) || "Failed to add"); }
    } });
    addPanel.append(
      el("label", { class: "flex items-center gap-2 text-xs text-gray-600" }, [allCb, el("span", { text: "Select / deselect all" })]),
      grid, addSelected,
    );
  }
  const exportBtn = el("button", {
    class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-white",
    text: "⬇ Excel report",
    title: "Download aircraft progress (same columns as this table, with status colors)",
    onclick: () => exportSurveillanceAircraftExcel(s, workingRows.length ? workingRows : aircraftRows, inspectors),
  });
  const matrixBtn = el("button", {
    class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-white",
    text: "Open items matrix",
    onclick: () => openSurveillanceMatrix(s),
  });
  const weeklyBtn = el("button", {
    class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-white",
    text: "Weekly report .eml",
    title: "Download Outlook draft: WEEKLY REPORT | OPERATOR | SURVEILLANCES | WEEK XX-YYYY",
    onclick: () => downloadWeeklySurveillanceReport(s),
  });
  wrap.append(
    el("div", { class: "flex items-center justify-between mb-1 gap-2 flex-wrap" }, [
      el("div", { class: "text-xs font-semibold text-gray-600 uppercase tracking-wide", text: `${cleanText(s.operator) || "Operator"} · ${s.quarter} ${s.year}` }),
      el("div", { class: "flex items-center gap-2" }, [
        weeklyBtn, matrixBtn, exportBtn, admin ? addBtn : null,
      ]),
    ]),
    addPanel,
  );

  if (!aircraftRows.length) {
    wrap.append(el("div", { class: "text-xs text-gray-400", text: "No aircraft attached yet — use ＋ Add aircraft." }));
    return wrap;
  }

  // ── Aircraft table ──
  // Order: MSN → Reg → Inspectors → LAUNCHED → PO# → … → Records link
  const headDefs = [
    { label: "", key: null, w: "2%" },
    { label: "MSN", key: "msn", w: "6%" },
    { label: "Reg", key: "reg", w: "6%" },
    { label: "Inspectors", key: "inspector", w: "12%" },
    { label: "Launch", key: "launched", w: "4%" },
    { label: "PO #", key: "po", w: "8%" },
    { label: "Location", key: "location", w: "8%" },
    { label: "Date", key: "date", w: "7%" },
    { label: "Physical", key: "physical", w: "8%" },
    { label: "Records", key: "records", w: "10%" },
    { label: "Link", key: "link", w: "4%" },
    { label: "AIR", key: "air", w: "8%" },
    { label: "Status", key: "status", w: "8%" },
    { label: "", key: null, w: "3%" },
  ];
  const sortState = { key: "msn", dir: "asc" };
  const thead = el("thead", { class: "bg-gray-50 text-gray-500 text-[11px]" });
  const tbody = el("tbody", {});
  const rowUpdaters = new Map();

  const sortValue = (row, key) => {
    if (key === "msn") return cleanText(row.msn) || "";
    if (key === "reg") return cleanText(row.registration) || "";
    if (key === "inspector") {
      return `${inspectorLabel(insMap, row.inspectorRecordsId)} / ${inspectorLabel(insMap, row.inspectorPhysicalId)}`;
    }
    if (key === "launched") return Boolean(row.atsAccess);
    if (key === "po") return `${cleanText(row.poRecords)} / ${cleanText(row.poPhysical)}`;
    if (key === "location") return cleanText(row.expectedLocation) || "";
    if (key === "date") return row.actualInspectionDate || row.expectedInspectionDate || "";
    if (key === "physical") return Math.round(Number(row.physicalPct) || 0);
    if (key === "records") return Math.round(Number(row.recordsPct) || 0);
    if (key === "link") return cleanText(row.airReportRecordsLink) ? 1 : 0;
    if (key === "air") return Math.round(Number(row.airReportPct) || 0);
    if (key === "status") return cleanText(displayOverallStatus(row)) || "";
    return "";
  };

  async function refresh() {
    try {
      const res = await api(`/api/surveillances/${s.id}`);
      const rows = res.aircraft || [];
      workingRows = rows;
      for (const a of rows) rowUpdaters.get(a.id)?.(a);
      parentUpdate?.(rows);
    } catch { /* keep last-known values on transient errors */ }
  }

  const paintHead = () => {
    thead.replaceChildren(
      el(
        "tr",
        {},
        headDefs.map((h) =>
          sortableTh(h.label, h.key, sortState, (key) => {
            toggleSortState(sortState, key);
            paintHead();
            paintBody();
          }, h.label === "Launch" || h.label === "Link" ? "text-center" : "")
        )
      )
    );
  };

  const paintBody = () => {
    tbody.replaceChildren();
    rowUpdaters.clear();
    const sorted = [...workingRows].sort((a, b) => {
      const cmp = compareSortValues(sortValue(a, sortState.key), sortValue(b, sortState.key));
      return sortState.dir === "asc" ? cmp : -cmp;
    });

    for (const a of sorted) {
      const caret = el("span", { class: "inline-block text-gray-400 transition-transform", text: "▸" });
      const insCell = el("td", { class: "px-1.5 py-1.5 text-[11px] text-gray-600 align-top" });
      const regCell = el("td", { class: "px-1.5 py-1.5 text-[11px] text-gray-700 align-top" });
      const launchedCell = el("td", { class: "px-1.5 py-1.5 align-top text-center" });
      const poCell = el("td", { class: "px-1.5 py-1.5 text-[11px] text-gray-600 align-top" });
      const locCell = el("td", { class: "px-1.5 py-1.5 text-[11px] align-top" });
      const dateCell = el("td", { class: "px-1.5 py-1.5 text-[11px] align-top" });
      const phyCell = el("td", { class: "px-1.5 py-1.5 align-bottom" });
      const recCell = el("td", { class: "px-1.5 py-1.5 align-bottom" });
      const linkCell = el("td", { class: "px-1.5 py-1.5 align-top text-center" });
      const airCell = el("td", { class: "px-1.5 py-1.5 align-bottom" });
      const statusCell = el("td", { class: "px-1.5 py-1.5 align-top" });
      const paintRow = (row) => {
        const dateAlert = inspectionDateAlert(row);
        const performed = inspectionPerformed(row);
        const recPct = Math.round(Number(row.recordsPct) || 0);
        dateCell.textContent = inspectionDateText(row);
        dateCell.className = `px-1.5 py-1.5 text-[11px] align-top ${dateAlert ? "text-red-600 font-semibold" : "text-gray-800"}`;
        dateCell.title = row.actualInspectionDate
          ? "Actual inspection date"
          : row.expectedInspectionDate
            ? "Expected inspection date"
            : "";
        regCell.textContent = cleanText(row.registration) || "—";
        insCell.replaceChildren(
          el("div", { text: `R: ${inspectorLabel(insMap, row.inspectorRecordsId)}` }),
          el("div", { text: `P: ${inspectorLabel(insMap, row.inspectorPhysicalId)}` }),
        );
        launchedCell.replaceChildren(
          greenLight(
            Boolean(row.atsAccess),
            row.atsAccess ? "Launched" : "Not launched"
          )
        );
        poCell.replaceChildren(
          el("div", { text: `R: ${cleanText(row.poRecords) || "—"}` }),
          el("div", { text: `P: ${cleanText(row.poPhysical) || "—"}` }),
        );
        locCell.textContent = cleanText(row.expectedLocation) || "—";
        const phyPct = Math.round(
          row.physicalMode === "manual"
            ? (Number(row.physicalReportPct) || Number(row.physicalPct) || 0)
            : (Number(row.physicalPct) || 0)
        );
        phyCell.replaceChildren(progressColumnStack([
          el("div", { class: "flex items-center gap-1 text-[10px] text-gray-600" }, [
            greenLight(performed, performed ? "Inspected" : "Not inspected"),
            el("span", { class: "leading-tight", text: performed ? "Inspected" : "Not inspected" }),
          ]),
          el("div", { class: "text-[10px] text-gray-600 leading-tight", text: `Photos ${phyPct}%` }),
        ], phyPct));
        const recState = recordsUploadStateFromPct(recPct);
        const recStateLabel = RECORDS_UPLOAD_STATES.find((s) => s.v === recState)?.t || "Not done";
        recCell.replaceChildren(progressColumnStack([
          el("div", { class: "flex items-center gap-1 text-[10px] text-gray-600" }, [
            recordsStatusLight(recState),
            el("span", { class: "leading-tight", text: `${recStateLabel} ${recPct}%` }),
          ]),
        ], recPct));
        linkCell.replaceChildren(
          cleanText(row.airReportRecordsLink)
            ? recordsLinkAnchor(row.airReportRecordsLink)
            : el("span", { class: "text-gray-300", text: "—" })
        );
        const airPct = Math.round(Number(row.airReportPct) || 0);
        const fastFieldsLink = cleanText(row.airReportPhysicalLink);
        airCell.replaceChildren(progressColumnStack([
          el("div", { class: "flex items-center justify-between gap-1 text-[10px] text-gray-600" }, [
            el("div", { class: "inline-flex items-center gap-0.5 min-w-0" }, [
              el("span", { text: "AIR" }),
              fastFieldsLink
                ? recordsLinkAnchor(fastFieldsLink)
                : null,
            ]),
            el("span", { class: "font-medium text-gray-800 shrink-0", text: `${airPct}%` }),
          ]),
        ], airPct));
        statusCell.replaceChildren(statusPill(displayOverallStatus(row)));
      };
      paintRow(a);
      rowUpdaters.set(a.id, paintRow);

      const removeCell = el("td", { class: "px-1.5 py-1.5 text-right" }, admin ? [
        el("button", { class: "text-xs text-gray-300 hover:text-red-600", title: "Remove from surveillance", text: "✕", onclick: async (e) => {
          e.stopPropagation();
          if (!confirm(`Remove ${cleanText(a.msn) || "this aircraft"} from the surveillance? Its records and photos will be deleted.`)) return;
          try { await api(`/api/surveillances/aircraft/${a.id}`, { method: "DELETE" }); rebuild(); }
          catch (err) { alert(cleanText(err?.message)); }
        } }),
      ] : []);

      const detailCell = el("td", { class: "p-0 bg-gray-50", colspan: String(headDefs.length) });
      const detailRow = el("tr", { class: "hidden border-t border-gray-100" }, [detailCell]);
      let built = false;
      const headRow = el("tr", {
        class: "border-t border-gray-100 hover:bg-gray-50 cursor-pointer",
        onclick: () => {
          const willOpen = detailRow.classList.contains("hidden");
          detailRow.classList.toggle("hidden");
          caret.style.transform = willOpen ? "rotate(90deg)" : "";
          if (willOpen && !built) { built = true; detailCell.replaceChildren(aircraftDetailInline(s, a, inspectors, insMap, refresh)); }
        },
      }, [
        el("td", { class: "px-1.5 py-1.5 w-6" }, [caret]),
        el("td", { class: "px-1.5 py-1.5 font-medium text-[12px]", text: cleanText(a.msn) || `#${a.aircraftId}` }),
        regCell, insCell, launchedCell, poCell, locCell, dateCell, phyCell, recCell, linkCell, airCell, statusCell, removeCell,
      ]);
      tbody.appendChild(headRow);
      tbody.appendChild(detailRow);
    }
  };

  paintHead();
  paintBody();
  const colgroup = el("colgroup", {}, headDefs.map((h) => el("col", { style: `width:${h.w || "auto"}` })));
  wrap.append(el("div", { class: "surv-fit-wrap bg-white rounded-lg border border-gray-200" }, [
    el("table", { class: "surv-fit-table w-full text-sm" }, [colgroup, thead, tbody]),
  ]));
  return wrap;
}

// Expanded MSN row: section 1 = inspectors/PO/date/location; section 2 = progress
// (physical done/not, records, AIR, link, comments); section 3 = checklist & photos.
function aircraftDetailInline(s, a, inspectors, insMap, refresh) {
  const saId = a.id;
  let latestAts = Boolean(a.atsAccess);
  const saveField = async (patch, flash) => {
    try { await saveAircraftPatch(saId, patch); flashSaved(flash); refresh(); }
    catch (e) { flashSaved(flash, false); }
  };

  const sectionTitle = (n, label) => el("div", {
    class: "text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-1",
    text: `${n}. ${label}`,
  });

  // ── 1. Inspectors · LAUNCHED · PO · date · location ──
  const canAdminister = Boolean(a.canEditPo);
  const recFlash = savedFlash();
  const recSel = inspectorSelect(inspectors, a.inspectorRecordsId, {
    class: "border rounded px-2 py-1 text-sm w-full",
    disabled: !canAdminister,
    onSave: (v) => saveField({ inspectorRecordsId: v }, recFlash),
  });
  const phyFlash = savedFlash();
  const phySel = inspectorSelect(inspectors, a.inspectorPhysicalId, {
    class: "border rounded px-2 py-1 text-sm w-full",
    disabled: !canAdminister,
    onSave: (v) => saveField({ inspectorPhysicalId: v }, phyFlash),
  });
  const atsFlash = savedFlash();
  const atsLight = greenLight(Boolean(a.atsAccess));
  const atsCb = el("input", { type: "checkbox", checked: Boolean(a.atsAccess) });
  atsCb.addEventListener("change", () => {
    latestAts = atsCb.checked;
    atsLight.className = `inline-block w-3 h-3 rounded-full align-middle ${atsCb.checked ? "bg-green-500" : "bg-gray-300"}`;
    saveField({ atsAccess: atsCb.checked }, atsFlash);
  });
  const atsField = el("label", { class: "flex items-center gap-2 text-sm h-full min-h-[2rem]" }, [
    atsCb, atsLight, el("span", { class: "text-xs text-gray-700", text: "LAUNCHED" }), atsFlash,
  ]);

  const canEditPo = canAdminister;
  const poRFlash = savedFlash();
  const poR = el("input", {
    type: "text", value: cleanText(a.poRecords) || "", placeholder: "PO Records",
    disabled: !canEditPo,
    title: canEditPo ? "Purchase order — admin editable" : "Purchase orders are read-only for inspectors",
    class: `border rounded px-2 py-1 text-sm w-full ${canEditPo ? "" : "bg-gray-100 text-gray-500 cursor-not-allowed"}`,
  });
  if (canEditPo) poR.addEventListener("blur", () => saveField({ poRecords: poR.value }, poRFlash));
  const poPFlash = savedFlash();
  const poP = el("input", {
    type: "text", value: cleanText(a.poPhysical) || "", placeholder: "PO Physical",
    disabled: !canEditPo,
    title: canEditPo ? "Purchase order — admin editable" : "Purchase orders are read-only for inspectors",
    class: `border rounded px-2 py-1 text-sm w-full ${canEditPo ? "" : "bg-gray-100 text-gray-500 cursor-not-allowed"}`,
  });
  if (canEditPo) poP.addEventListener("blur", () => saveField({ poPhysical: poP.value }, poPFlash));
  const dateFlash = savedFlash();
  const dateInput = el("input", { type: "date", value: a.expectedInspectionDate ? fmtDate(a.expectedInspectionDate) : "", class: "border rounded px-2 py-1 text-sm w-full" });
  dateInput.addEventListener("change", () => saveField({ expectedInspectionDate: dateInput.value || null }, dateFlash));
  const locFlash = savedFlash();
  const locInput = el("input", { type: "text", value: cleanText(a.expectedLocation) || "", placeholder: "Airport / location", class: "border rounded px-2 py-1 text-sm w-full" });
  locInput.addEventListener("blur", () => saveField({ expectedLocation: locInput.value }, locFlash));

  const peopleSection = el("div", { class: "space-y-3" }, [
    sectionTitle("1", "Inspectors · LAUNCHED · PO · date · location"),
    el("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-3" }, [
      fieldFlash("Records inspector", recSel, recFlash),
      fieldFlash("Physical inspector", phySel, phyFlash),
      el("div", { class: "flex flex-col gap-1 justify-end" }, [atsField]),
    ]),
    el("div", { class: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" }, [
      fieldFlash("PO — Records", poR, poRFlash),
      fieldFlash("PO — Physical", poP, poPFlash),
      fieldFlash("Inspection date", dateInput, dateFlash),
      fieldFlash("Location", locInput, locFlash),
    ]),
  ]);

  // Component serials → ATS filename root for engine / APU|prop / LG docs
  const isAtr = Boolean(a.isAtr) || /ATR/i.test(`${a.aircraftType || ""} ${a.aircraftSeries || ""}`);
  const serialFields = [
    { key: "eng1Serial", label: "ENG 1", placeholder: "ENG 1", value: a.eng1Serial },
    { key: "eng2Serial", label: "ENG 2", placeholder: "ENG 2", value: a.eng2Serial },
    ...(isAtr
      ? [
          { key: "lhPropSerial", label: "LH PROP", placeholder: "LH PROP", value: a.lhPropSerial },
          { key: "rhPropSerial", label: "RH PROP", placeholder: "RH PROP", value: a.rhPropSerial },
        ]
      : [
          { key: "apuSerial", label: "APU", placeholder: "APU", value: a.apuSerial },
        ]),
    { key: "lhMlgSerial", label: "LH MLG", placeholder: "LH MLG", value: a.lhMlgSerial },
    { key: "rhMlgSerial", label: "RH MLG", placeholder: "RH MLG", value: a.rhMlgSerial },
    { key: "nlgSerial", label: "NLG", placeholder: "NLG", value: a.nlgSerial },
  ];
  const serialInputs = serialFields.map((f) => {
    const flash = savedFlash();
    const input = el("input", {
      type: "text",
      value: cleanText(f.value) || "",
      placeholder: f.placeholder,
      class: "border rounded px-2 py-1 text-sm w-full font-mono",
      title: "Used as the first segment of the ATS export filename for this component",
    });
    input.addEventListener("blur", () => saveField({ [f.key]: input.value.trim() || null }, flash));
    return fieldFlash(f.label, input, flash);
  });
  const serialsSection = el("div", { class: "space-y-3" }, [
    el("div", {
      class: "text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-1",
      text: isAtr
        ? "Component serials (engines · propellers · landing gear)"
        : "Component serials (engines · APU · landing gear)",
    }),
    el("p", {
      class: "text-[11px] text-gray-500",
      text: isAtr
        ? "ATR: no APU — enter LH/RH propeller serials. Empty fields use the generic label in ATS filenames."
        : "Prefill from materials ESNs when available. Empty fields use the generic label (ENG 1, LH MLG, …) in ATS filenames until you enter a serial.",
    }),
    el("div", { class: "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" }, serialInputs),
  ]);

  // ── 2. Progress: physical done/not · records · AIR · link · comments ──
  const recPctNow = Math.round(Number(a.recordsPct) || 0);
  const phyPctNow = Math.round(
    a.physicalMode === "manual"
      ? (Number(a.physicalReportPct) || Number(a.physicalPct) || 0)
      : (Number(a.physicalPct) || 0)
  );

  const perfFlash = savedFlash();
  const perfLight = greenLight(a.physicalStatus === "Performed");
  const perfCb = el("input", { type: "checkbox", checked: a.physicalStatus === "Performed" });
  perfCb.addEventListener("change", () => {
    perfLight.className = `inline-block w-3 h-3 rounded-full align-middle ${perfCb.checked ? "bg-green-500" : "bg-gray-300"}`;
    saveField({ physicalStatus: perfCb.checked ? "Performed" : "Scheduled" }, perfFlash);
  });
  const perfField = el("label", { class: "flex items-center gap-2 text-sm h-full min-h-[2rem]" }, [
    perfCb, perfLight, el("span", { class: "text-xs text-gray-700", text: "Physical inspection performed" }), perfFlash,
  ]);

  // Photos progress always visible: folder-driven bar, or manual % selector.
  const phyPctFlash = savedFlash();
  const phyPctSel = progressPctSelect(phyPctNow, { className: "border rounded px-2 py-1 text-sm w-full" });
  phyPctSel.addEventListener("change", () =>
    saveField({ physicalMode: "manual", physicalReportPct: Number(phyPctSel.value) }, phyPctFlash)
  );
  const phyFoldersBarHost = el("div", { class: "space-y-1" }, [
    el("div", { class: "text-xs text-gray-600", text: `Photos ${phyPctNow}%` }),
    progressBar(phyPctNow),
  ]);
  const phyModeFlash = savedFlash();
  const phyModeSel = el("select", { class: "border rounded px-2 py-1 text-sm w-full" }, [
    el("option", { value: "folders", text: "From photo folders / N/A", selected: a.physicalMode !== "manual" }),
    el("option", { value: "manual", text: "Manual photo progress %", selected: a.physicalMode === "manual" }),
  ]);
  phyModeSel.addEventListener("change", () => saveField({ physicalMode: phyModeSel.value }, phyModeFlash));

  const recModeFlash = savedFlash();
  const recModeSel = el("select", { class: "border rounded px-2 py-1 text-sm w-full" }, [
    el("option", { value: "checklist", text: "From checklist (default)", selected: a.recordsMode !== "direct_ats" }),
    el("option", { value: "direct_ats", text: "Manual records progress %", selected: a.recordsMode === "direct_ats" }),
  ]);
  recModeSel.addEventListener("change", () => saveField({ recordsMode: recModeSel.value }, recModeFlash));

  const recFineFlash = savedFlash();
  const recFineSel = progressPctSelect(recPctNow, { className: "border rounded px-2 py-1 text-sm w-full" });
  const recFineWrap = el("div", {
    class: a.recordsMode === "direct_ats" ? "" : "hidden",
  }, [fieldFlash("Records progress %", recFineSel, recFineFlash)]);
  recFineSel.addEventListener("change", () =>
    saveField({ recordsMode: "direct_ats", recordsReportPct: Number(recFineSel.value) }, recFineFlash)
  );
  recModeSel.addEventListener("change", () => {
    recFineWrap.classList.toggle("hidden", recModeSel.value !== "direct_ats");
  });

  const airFlash = savedFlash();
  const airPctSel = progressPctSelect(a.airReportPct, { className: "border rounded px-2 py-1 text-sm w-full" });
  airPctSel.addEventListener("change", () => saveField({ airReportPct: Number(airPctSel.value) }, airFlash));

  const linkFlash = savedFlash();
  const linkInput = el("input", {
    type: "url",
    value: cleanText(a.airReportRecordsLink) || "",
    placeholder: "https://… records folder / share link",
    class: "border rounded px-2 py-1 text-sm w-full",
  });
  linkInput.addEventListener("blur", () => saveField({ airReportRecordsLink: linkInput.value }, linkFlash));

  const ffFlash = savedFlash();
  const ffInput = el("input", {
    type: "url",
    value: cleanText(a.airReportPhysicalLink) || "",
    placeholder: "https://… Fast Fields follow-up",
    class: "border rounded px-2 py-1 text-sm w-full",
  });
  ffInput.addEventListener("blur", () => saveField({ airReportPhysicalLink: ffInput.value }, ffFlash));

  const commentsHost = el("div", { class: "space-y-2" });
  const paintComments = async () => {
    try {
      const res = await api(`/api/surveillances/aircraft/${saId}/comments`);
      const list = res.comments || [];
      const addFlash = savedFlash();
      const ta = el("textarea", { rows: "2", placeholder: "Add a comment…", class: "border rounded px-2 py-1 text-sm w-full" });
      const addBtn = el("button", {
        type: "button",
        class: "text-xs text-white bg-dae rounded px-2 py-1",
        text: "Add comment",
        onclick: async () => {
          const body = ta.value.trim();
          if (!body) return;
          try {
            await api(`/api/surveillances/aircraft/${saId}/comments`, jsonBody("POST", { body }));
            flashSaved(addFlash);
            ta.value = "";
            paintComments();
            refresh();
          } catch (e) {
            flashSaved(addFlash, false);
            alert(cleanText(e?.message));
          }
        },
      });
      commentsHost.replaceChildren(
        el("div", { class: "flex items-start gap-2" }, [ta, el("div", { class: "flex flex-col gap-1" }, [addBtn, addFlash])]),
        list.length
          ? el("div", { class: "space-y-1 max-h-40 overflow-y-auto" }, list.map((c) =>
              el("div", { class: "flex items-start justify-between gap-2 text-xs bg-white border border-gray-100 rounded px-2 py-1" }, [
                el("div", {}, [
                  el("div", { class: "text-gray-800 whitespace-pre-wrap", text: c.body }),
                  el("div", { class: "text-[10px] text-gray-400 mt-0.5", text: `${c.createdBy || "—"} · ${fmtDate(c.createdAt)}` }),
                ]),
                el("button", {
                  class: "text-gray-300 hover:text-red-600 shrink-0",
                  text: "✕",
                  title: "Delete comment",
                  onclick: async () => {
                    if (!confirm("Delete this comment?")) return;
                    try {
                      await api(`/api/surveillances/comments/${c.id}`, { method: "DELETE" });
                      paintComments();
                    } catch (e) { alert(cleanText(e?.message)); }
                  },
                }),
              ])
            ))
          : el("div", { class: "text-[11px] text-gray-400", text: "No comments yet." }),
      );
    } catch (e) {
      commentsHost.replaceChildren(el("div", { class: "text-xs text-red-600", text: cleanText(e?.message) || "Could not load comments" }));
    }
  };
  paintComments();

  const phyManualWrap = el("div", {
    class: a.physicalMode === "manual" ? "" : "hidden",
  }, [fieldFlash("Photos / physical progress %", phyPctSel, phyPctFlash)]);
  const phyFoldersWrap = el("div", {
    class: a.physicalMode === "manual" ? "hidden" : "",
  }, [phyFoldersBarHost]);
  phyModeSel.addEventListener("change", () => {
    const manual = phyModeSel.value === "manual";
    phyManualWrap.classList.toggle("hidden", !manual);
    phyFoldersWrap.classList.toggle("hidden", manual);
  });

  const progressSection = el("div", { class: "space-y-3" }, [
    sectionTitle("2", "Progress — physical · records · AIR · comments"),
    el("div", { class: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" }, [
      el("div", { class: "flex flex-col gap-1 justify-end" }, [perfField]),
      fieldFlash("Physical tracking", phyModeSel, phyModeFlash),
      phyFoldersWrap,
      phyManualWrap,
      fieldFlash("Records tracking", recModeSel, recModeFlash),
      recFineWrap,
      fieldFlash("AIR report progress", airPctSel, airFlash),
      fieldFlash("Records link", linkInput, linkFlash),
      fieldFlash("Fast Fields link (AIR follow-up)", ffInput, ffFlash),
    ]),
    el("div", {}, [
      el("div", { class: "text-xs text-gray-500 mb-1", text: "Comments (append-only log)" }),
      commentsHost,
    ]),
  ]);

  const mainForm = el("div", { class: "p-3 bg-gray-50 space-y-5" }, [
    peopleSection,
    serialsSection,
    progressSection,
  ]);

  // ── 3. Checklist & photos (default open; checklist is default tracking) ──
  const tabState = { tab: "records", open: true, built: false };
  const tabBtnClass = (active) => `px-3 py-1.5 text-sm font-medium border-b-2 ${active ? "border-dae text-dae" : "border-transparent text-gray-500"}`;
  const recBtn = el("button", { class: tabBtnClass(true), text: "Records checklist", onclick: () => { tabState.tab = "records"; drawTab(); } });
  const phyBtn = el("button", { class: tabBtnClass(false), text: "Physical photos", onclick: () => { tabState.tab = "physical"; drawTab(); } });
  const tabbar = el("div", { class: "flex gap-2 border-b border-gray-200 px-3" }, [recBtn, phyBtn]);
  const tabBody = el("div", { class: "p-3" });
  const detailInner = el("div", { class: "border-t border-gray-100" }, [tabbar, tabBody]);
  const detailToggle = el("button", {
    class: "w-full text-left px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 border-t border-gray-100",
    text: "▾ 3. Checklist & photos",
    onclick: () => {
      tabState.open = !tabState.open;
      detailInner.classList.toggle("hidden", !tabState.open);
      detailToggle.textContent = tabState.open
        ? "▾ 3. Checklist & photos"
        : "▸ 3. Checklist & photos";
      if (tabState.open && !tabState.built) {
        tabState.built = true;
        drawTab();
      }
    },
  });
  function drawTab() {
    recBtn.className = tabBtnClass(tabState.tab === "records");
    phyBtn.className = tabBtnClass(tabState.tab === "physical");
    const body = el("div", {}, [loadingNode()]);
    tabBody.replaceChildren(body);
    if (tabState.tab === "records") initRecords(body, saId, () => { latestAts = Boolean(atsCb.checked); refresh(); }, { atsAccess: latestAts });
    else initPhysical(body, saId, refresh);
  }
  // Default: open checklist
  tabState.built = true;
  drawTab();

  return el("div", { class: "border-t border-gray-100" }, [mainForm, detailToggle, detailInner]);
}

// Field label with an inline save indicator to the right of the label.
function fieldFlash(label, control, flash) {
  const top = el("div", { class: "flex items-center gap-1" }, [
    el("span", { class: "text-xs text-gray-500", text: label }), flash || null,
  ]);
  return el("label", { class: "flex flex-col gap-1" }, [top, control]);
}

// ---- Create / Edit ---------------------------------------------------------

// Create-only: pick operator + quarter/year/finalization + which MSNs to include.
// All per-aircraft detail (inspectors, dates, PO, ATS, comments) is edited inline on
// the dashboard after creation.
function initEditor(host) {
  const operators = operatorsFromFleet();
  const form = { operatorId: "", quarter: "Q1", year: new Date().getUTCFullYear(), expectedFinalizationDate: "", picks: new Set() };

  const rowsHost = el("div", { class: "space-y-2" });
  const opSel = el("select", { class: "border rounded-lg px-3 py-2 text-sm", onchange: (e) => { form.operatorId = e.target.value; form.picks.clear(); renderRows(); } },
    [el("option", { value: "", text: "Select operator…" }), ...operators.map((o) => el("option", { value: String(o.id), text: o.name }))]);

  function renderRows() {
    const fleet = aircraftForOperator(form.operatorId);
    if (!form.operatorId) { rowsHost.replaceChildren(el("div", { class: "text-sm text-gray-400", text: "Select an operator to list its MSNs." })); return; }
    if (!fleet.length) { rowsHost.replaceChildren(el("div", { class: "text-sm text-gray-400", text: "No aircraft with MSNs found for this operator in the fleet." })); return; }
    const checks = [];
    const allCb = el("input", { type: "checkbox", onchange: () => { form.picks.clear(); if (allCb.checked) fleet.forEach((f) => form.picks.add(f.id)); checks.forEach((c) => (c.checked = allCb.checked)); } });
    const list = fleet.map((f) => {
      const cb = el("input", { type: "checkbox", checked: form.picks.has(f.id), onchange: (e) => { e.target.checked ? form.picks.add(f.id) : form.picks.delete(f.id); } });
      checks.push(cb);
      return el("label", { class: "flex items-center gap-2 text-sm border border-gray-200 rounded-lg p-2" }, [cb, el("span", { text: `${f.msn}${f.registration ? ` · ${f.registration}` : ""}` })]);
    });
    rowsHost.replaceChildren(
      el("label", { class: "flex items-center gap-2 text-xs text-gray-600 mb-2" }, [allCb, el("span", { text: "Select / deselect all" })]),
      el("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-2" }, list),
    );
  }

  const saveBtn = el("button", { class: "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90", text: "Create surveillance", onclick: () => save() });
  async function save() {
    if (!form.operatorId) { alert("Select an operator"); return; }
    saveBtn.disabled = true; saveBtn.textContent = "Creating…";
    try {
      const res = await api("/api/surveillances", jsonBody("POST", {
        quarter: form.quarter, year: Number(form.year), operatorId: Number(form.operatorId),
        expectedFinalizationDate: form.expectedFinalizationDate || null,
        aircraft: [...form.picks].map((id) => ({ aircraftId: id })),
      }));
      go(`#/surveillances?expand=${res.id}`);
    } catch (e) {
      alert(cleanText(e?.message) || "Create failed");
      saveBtn.disabled = false; saveBtn.textContent = "Create surveillance";
    }
  }

  host.replaceChildren(
    pageShell("New surveillance", "One operator, one or more MSNs", [
      el("button", { class: "px-3 py-2 text-sm border border-gray-200 rounded-lg", text: "← Cancel", onclick: () => go("#/surveillances") }),
    ]),
    el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 space-y-4" }, [
      el("div", { class: "grid grid-cols-2 md:grid-cols-4 gap-3" }, [
        field("Operator", opSel),
        field("Quarter", el("select", { class: "border rounded-lg px-3 py-2 text-sm", onchange: (e) => (form.quarter = e.target.value) }, ["Q1", "Q2", "Q3", "Q4"].map((q) => el("option", { value: q, text: q, selected: q === form.quarter })))),
        field("Year", el("input", { type: "number", value: String(form.year), class: "border rounded-lg px-3 py-2 text-sm", onchange: (e) => (form.year = e.target.value) })),
        field("Expected finalization", el("input", { type: "date", class: "border rounded-lg px-3 py-2 text-sm", onchange: (e) => (form.expectedFinalizationDate = e.target.value) })),
      ]),
      el("div", { class: "text-sm font-semibold text-gray-800", text: "Aircraft (MSNs)" }),
      rowsHost,
      el("div", { class: "pt-2" }, [saveBtn]),
    ])
  );
  renderRows();
}

function field(label, control) {
  return el("label", { class: "flex flex-col gap-1" }, [el("span", { class: "text-xs text-gray-500", text: label }), control]);
}

// ---- Surveillance detail ---------------------------------------------------

async function initSurveillanceDetail(host, id) {
  try {
    const res = await api(`/api/surveillances/${id}`);
    const s = res.surveillance;
    const rows = res.aircraft || [];
    const sortState = { key: "msn", dir: "asc" };
    const headDefs = [
      { label: "MSN", key: "msn" },
      { label: "Inspectors (R / P)", key: "inspector" },
      { label: "LAUNCHED", key: "launched" },
      { label: "PO (R / P)", key: "po" },
      { label: "Records", key: "records" },
      { label: "Physical", key: "physical" },
      { label: "Link", key: "link" },
      { label: "Overall", key: "status" },
      { label: "Exp. date", key: "date" },
      { label: "", key: null },
    ];
    const sortValue = (a, key) => {
      if (key === "msn") return cleanText(a.msn) || cleanText(a.registration) || "";
      if (key === "inspector") return `${cleanText(a.inspectorRecordsName)} / ${cleanText(a.inspectorPhysicalName)}`;
      if (key === "launched") return Boolean(a.atsAccess);
      if (key === "po") return `${cleanText(a.poRecords)} / ${cleanText(a.poPhysical)}`;
      if (key === "records") return Math.round(Number(a.recordsPct) || 0);
      if (key === "physical") return Math.round(Number(a.physicalPct) || 0);
      if (key === "link") return cleanText(a.airReportRecordsLink) ? 1 : 0;
      if (key === "status") return cleanText(a.overallStatus) || "";
      if (key === "date") return a.actualInspectionDate || a.expectedInspectionDate || "";
      return "";
    };
    const thead = el("thead", { class: "bg-gray-50 text-gray-600" });
    const tbody = el("tbody");
    const paint = () => {
      thead.replaceChildren(
        el(
          "tr",
          {},
          headDefs.map((h) =>
            sortableTh(h.label, h.key, sortState, (key) => {
              toggleSortState(sortState, key);
              paint();
            })
          )
        )
      );
      const sorted = [...rows].sort((a, b) => {
        const cmp = compareSortValues(sortValue(a, sortState.key), sortValue(b, sortState.key));
        return sortState.dir === "asc" ? cmp : -cmp;
      });
      tbody.replaceChildren(
        ...sorted.map((a) => {
          const dateAlert = inspectionDateAlert(a);
          return el("tr", { class: "border-t border-gray-100 hover:bg-gray-50 cursor-pointer", onclick: () => go(`#/surveillances/${id}/aircraft/${a.id}`) }, [
            el("td", { class: "px-3 py-2 font-medium", text: cleanText(a.msn) || cleanText(a.registration) || `#${a.aircraftId}` }),
            el("td", { class: "px-3 py-2 text-xs text-gray-600", text: `${cleanText(a.inspectorRecordsName) || "—"} / ${cleanText(a.inspectorPhysicalName) || "—"}` }),
            el("td", { class: "px-3 py-2 text-center" }, [
              greenLight(Boolean(a.atsAccess), a.atsAccess ? "Launched" : "Not launched"),
            ]),
            el("td", { class: "px-3 py-2 text-xs", text: `${cleanText(a.poRecords) || "—"} / ${cleanText(a.poPhysical) || "—"}` }),
            el("td", { class: "px-3 py-2 w-28" }, [progressBar(a.recordsPct)]),
            el("td", { class: "px-3 py-2 w-28" }, [progressBar(a.physicalPct)]),
            el("td", { class: "px-3 py-2 text-center" }, [
              cleanText(a.airReportRecordsLink) ? recordsLinkAnchor(a.airReportRecordsLink) : el("span", { class: "text-gray-300", text: "—" }),
            ]),
            el("td", { class: "px-3 py-2" }, [statusPill(a.overallStatus)]),
            el("td", {
              class: `px-3 py-2 ${dateAlert ? "text-red-600 font-semibold" : ""}`,
              text: fmtDateDdMmmYy(a.actualInspectionDate || a.expectedInspectionDate),
              title: a.actualInspectionDate ? "Actual inspection date" : "Expected inspection date",
            }),
            el("td", { class: "px-3 py-2 text-dae text-xs", text: "Open →" }),
          ]);
        })
      );
    };
    paint();
    const table = el("div", { class: "bg-white rounded-xl border border-gray-200 overflow-hidden" }, [
      el("table", { class: "w-full text-sm" }, [thead, tbody]),
    ]);
    const avgR = Math.round(rows.reduce((s2, r) => s2 + Number(r.recordsPct || 0), 0) / (rows.length || 1));
    const avgP = Math.round(rows.reduce((s2, r) => s2 + Number(r.physicalPct || 0), 0) / (rows.length || 1));
    const fleetCompletion = Math.round((avgR + avgP) / 2);
    host.replaceChildren(
      pageShell(`${s.quarter} ${s.year} — ${cleanText(s.operator) || "Operator"}`, `Expected finalization: ${fmtDateDdMmmYy(s.expectedFinalizationDate)}${isOverdue(s.expectedFinalizationDate, s.status) ? " (overdue)" : ""}`, [
        el("button", { class: "px-3 py-2 text-sm border border-gray-200 rounded-lg", text: "← All surveillances", onclick: () => go("#/surveillances") }),
        el("button", { class: "px-3 py-2 text-sm border border-gray-200 rounded-lg", text: "Add / edit aircraft", onclick: () => go(`#/surveillances/${id}/edit`) }),
      ]),
      el("div", { class: "grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" }, [
        card("Aircraft", String(rows.length)),
        card("Records progress", `${avgR}%`),
        card("Physical progress", `${avgP}%`),
        card("Completion", `${fleetCompletion}%`, "50% records + 50% physical"),
      ]),
      table,
    );
  } catch (e) {
    host.replaceChildren(errorNode(e));
  }
}

// ---- Aircraft detail (Records + Physical) ----------------------------------

async function initAircraftDetail(host, surveillanceId, saId) {
  const tabState = { tab: "records" };
  function shell(body) {
    const tabBtn = (id, label) => el("button", {
      class: `px-4 py-2 text-sm font-medium border-b-2 ${tabState.tab === id ? "border-dae text-dae" : "border-transparent text-gray-500"}`,
      text: label, onclick: () => { tabState.tab = id; draw(); },
    });
    host.replaceChildren(
      pageShell("Aircraft inspection", "", [
        el("button", { class: "px-3 py-2 text-sm border border-gray-200 rounded-lg", text: "← Surveillance", onclick: () => go(`#/surveillances/${surveillanceId}`) }),
      ]),
      el("div", { class: "flex gap-2 border-b border-gray-200 mb-4" }, [tabBtn("records", "Records"), tabBtn("physical", "Physical")]),
      body,
    );
  }
  function draw() {
    const body = el("div", {}, [loadingNode()]);
    shell(body);
    if (tabState.tab === "records") initRecords(body, saId);
    else initPhysical(body, saId);
  }
  draw();
}

/** Shared progress selector options: 0%, 5%, …, 100%. */
const PROGRESS_STEPS_5 = Array.from({ length: 21 }, (_, i) => {
  const v = i * 5;
  const label =
    v === 0 ? "Not started (0%)" :
    v === 100 ? "Complete (100%)" :
    `${v}%`;
  return { v, t: label };
});

function nearestProgressStep(pct) {
  const n = Math.round(Number(pct) || 0);
  return PROGRESS_STEPS_5.reduce(
    (best, step) => (Math.abs(step.v - n) < Math.abs(best.v - n) ? step : best),
    PROGRESS_STEPS_5[0],
  );
}

function progressPctSelect(currentPct, { className = "border rounded px-2 py-1.5 text-sm w-full max-w-xs" } = {}) {
  const nearest = nearestProgressStep(currentPct);
  return el(
    "select",
    { class: className },
    PROGRESS_STEPS_5.map((o) => el("option", { value: String(o.v), text: o.t, selected: o.v === nearest.v })),
  );
}

async function initRecords(host, saId, onMutate, { atsAccess = false } = {}) {
  try {
    const res = await api(`/api/surveillances/aircraft/${saId}/records`);
    const items = res.items || [];
    const mode = res.recordsMode === "direct_ats" ? "direct_ats" : "checklist";
    const atsOn = typeof res.atsAccess === "boolean" ? res.atsAccess : atsAccess;
    const closed = items.filter((i) => i.status === "provided" || i.status === "already_in_ats").length;
    const willNot = items.filter((i) => i.status === "will_not_be_provided").length;
    const progressed = closed + willNot;
    const checklistPct = items.length === 0 ? 100 : Math.round((progressed / items.length) * 100);
    const reportPct = Math.round(Number(res.recordsReportPct) || 0);
    const pct = mode === "direct_ats" ? reportPct : checklistPct;

    const afterChange = () => { initRecords(host, saId, onMutate, { atsAccess: atsOn }); onMutate?.(); };

    const modeFlash = savedFlash();
    const modeSel = el("select", { class: "border rounded px-2 py-1 text-sm" }, [
      el("option", { value: "checklist", text: "Track checklist items", selected: mode === "checklist" }),
      el("option", { value: "direct_ats", text: "Manual progress % (keep uploaded files)", selected: mode === "direct_ats" }),
    ]);
    modeSel.addEventListener("change", async () => {
      try {
        await saveAircraftPatch(saId, { recordsMode: modeSel.value });
        flashSaved(modeFlash);
        afterChange();
      } catch (e) {
        flashSaved(modeFlash, false);
        alert(cleanText(e?.message) || "Could not update records mode");
      }
    });

    const uploadState = recordsUploadStateFromPct(pct);
    const msnLabel = cleanText(res.msn) || `SA ${saId}`;
    const exportBtn = el("button", {
      class: "text-xs text-white bg-dae rounded px-2 py-1 hover:bg-dae/90",
      text: `Export ATS ZIP · MSN ${msnLabel}`,
      title: "Download records + photos for this MSN only",
      onclick: () => exportZip(saId, { msn: msnLabel }),
    });
    const header = el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 mb-3 space-y-3" }, [
      el("div", { class: "flex flex-wrap items-center justify-between gap-2 text-sm" }, [
        el("div", { class: "flex flex-wrap items-center gap-3" }, [
          el("span", { class: "font-semibold", text: "Records completion" }),
          el("span", { class: "inline-flex items-center gap-1.5 text-xs text-gray-600" }, [
            recordsStatusLight(uploadState),
            el("span", {
              text:
                uploadState === "uploaded" ? "Complete" :
                uploadState === "in_progress" ? "In progress" :
                "Not started",
            }),
          ]),
          el("span", { class: "inline-flex items-center gap-1 text-xs text-gray-600" }, [
            greenLight(Boolean(atsOn), atsOn ? "Launched" : "Not launched"),
            el("span", { text: "Launched" }),
          ]),
        ]),
        el("div", { class: "flex items-center gap-2" }, [
          el("span", {
            class: "font-medium text-gray-800",
            text: mode === "direct_ats" ? `${pct}%` : `${pct}% (${progressed}/${items.length})`,
          }),
          exportBtn,
        ]),
      ]),
      progressBar(pct),
      el("div", { class: "flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100" }, [
        el("span", { class: "text-xs text-gray-500", text: "Tracking mode" }),
        modeSel,
        modeFlash,
        el("span", {
          class: "text-[10px] text-gray-400",
          text: "Checklist rules when selected; manual % when selected. Uploaded files are kept either way.",
        }),
      ]),
    ]);

    if (mode === "direct_ats") {
      const reportFlash = savedFlash();
      const reportSel = progressPctSelect(reportPct);
      reportSel.addEventListener("change", async () => {
        try {
          await saveAircraftPatch(saId, { recordsReportPct: Number(reportSel.value) });
          flashSaved(reportFlash);
          afterChange();
        } catch (e) {
          flashSaved(reportFlash, false);
          alert(cleanText(e?.message) || "Could not update report progress");
        }
      });
      host.replaceChildren(
        header,
        el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 space-y-3" }, [
          el("div", { class: "text-sm font-medium text-gray-800", text: "Manual records progress" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Checklist item statuses do not drive the row %. Uploaded files remain available if you switch back to checklist tracking.",
          }),
          fieldFlash("Report progress", reportSel, reportFlash),
        ]),
        el("div", { class: "bg-white rounded-xl border border-gray-200 mt-3" }, [
          el("div", { class: "px-4 py-2 text-xs text-gray-500 border-b", text: "Checklist files (read/upload still available)" }),
          el("div", { class: "divide-y divide-gray-100" }, items.map((it) => rowForItem(it, saId, afterChange))),
        ]),
      );
      return;
    }

    const list = el("div", { class: "divide-y divide-gray-100" }, items.map((it) => rowForItem(it, saId, afterChange)));
    host.replaceChildren(
      header,
      el("div", { class: "bg-white rounded-xl border border-gray-200" }, [list]),
    );
  } catch (e) {
    host.replaceChildren(errorNode(e));
  }
}

function recordsStatusSelectClass(status) {
  if (status === "provided" || status === "already_in_ats") return "border rounded px-2 py-1 text-xs bg-green-50 text-green-800 border-green-200";
  if (status === "will_not_be_provided") return "border rounded px-2 py-1 text-xs bg-gray-100 text-gray-600 border-gray-200";
  return "border rounded px-2 py-1 text-xs bg-red-50 text-red-700 border-red-200";
}

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Modal: one document date per file (default today). Returns Map(file → YYYY-MM-DD) or null if cancelled. */
function promptDocumentDates(files) {
  const list = [...(files || [])].filter(Boolean);
  if (!list.length) return Promise.resolve(null);
  const today = todayYmdLocal();

  return new Promise((resolve) => {
    const overlay = el("div", {
      class: "fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4",
      role: "dialog",
      "aria-modal": "true",
    });
    const rowsHost = el("div", { class: "space-y-3 max-h-[55vh] overflow-y-auto pr-1" });
    const inputs = list.map((file, idx) => {
      const dateInput = el("input", {
        type: "date",
        value: today,
        class: "border border-gray-300 rounded-lg px-3 py-2 text-sm w-full max-w-[14rem]",
        title: "Document date (YYYY-MM-DD)",
      });
      const wrap = el("div", { class: "grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center border border-gray-100 rounded-lg px-3 py-2 bg-gray-50" }, [
        el("div", { class: "min-w-0" }, [
          el("div", { class: "text-sm text-gray-800 truncate", text: file.name || `File ${idx + 1}`, title: file.name }),
          el("div", { class: "text-[11px] text-gray-400", text: "Example date format: YYYY-MM-DD (e.g. 2026-07-31)" }),
        ]),
        dateInput,
      ]);
      rowsHost.appendChild(wrap);
      return { file, dateInput };
    });

    const close = (result) => {
      overlay.remove();
      resolve(result);
    };

    const applyAll = el("input", {
      type: "date",
      value: today,
      class: "border border-gray-300 rounded-lg px-2 py-1.5 text-sm",
      title: "Set all dates",
    });
    applyAll.addEventListener("change", () => {
      for (const row of inputs) row.dateInput.value = applyAll.value || today;
    });

    const panel = el("div", { class: "bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col" }, [
      el("div", { class: "px-5 py-4 border-b border-gray-200" }, [
        el("div", { class: "text-lg font-semibold text-gray-900", text: "Document date" }),
        el("div", {
          class: "text-xs text-gray-500 mt-1",
          text: "Each file needs a document date for the ATS name. Default is today — change per file if needed.",
        }),
      ]),
      el("div", { class: "px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2 text-xs text-gray-600" }, [
        el("span", { text: "Set all to" }),
        applyAll,
        el("span", { class: "text-gray-400", text: "· type or pick a year in the calendar" }),
      ]),
      el("div", { class: "px-5 py-4 flex-1 overflow-hidden" }, [rowsHost]),
      el("div", { class: "px-5 py-3 border-t border-gray-200 flex justify-end gap-2" }, [
        el("button", {
          type: "button",
          class: "px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50",
          text: "Cancel",
          onclick: () => close(null),
        }),
        el("button", {
          type: "button",
          class: "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
          text: list.length === 1 ? "Upload" : `Upload ${list.length} files`,
          onclick: () => {
            const out = new Map();
            for (const row of inputs) {
              const v = (row.dateInput.value || "").trim();
              if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                alert(`Please enter a valid date (YYYY-MM-DD) for “${row.file.name || "file"}”.`);
                return;
              }
              out.set(row.file, v);
            }
            close(out);
          },
        }),
      ]),
    ]);
    overlay.appendChild(panel);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(null); });
    document.body.appendChild(overlay);
    inputs[0]?.dateInput?.focus();
  });
}

function rowForItem(it, saId, afterChange) {
  const statusSel = el("select", { class: recordsStatusSelectClass(it.status) }, [
    { v: "not_yet_provided", t: "Not yet provided" },
    { v: "provided", t: "Provided" },
    { v: "already_in_ats", t: "Already in ATS" },
    { v: "will_not_be_provided", t: "Will not be provided" },
  ].map((o) => el("option", { value: o.v, text: o.t, selected: o.v === it.status })));
  statusSel.addEventListener("change", async () => {
    try {
      await api(
        `/api/surveillances/aircraft/${saId}/records/${it.checklistItemId}`,
        jsonBody("PUT", {
          status: statusSel.value,
          componentKey: it.componentKey != null ? it.componentKey : "",
        }),
      );
      statusSel.className = recordsStatusSelectClass(statusSel.value);
      afterChange?.();
    }
    catch (e) { alert(cleanText(e?.message)); }
  });

  const rawFiles = it.files;
  let files = rawFiles;
  if (typeof files === "string") {
    try { files = JSON.parse(files); } catch { files = []; }
  }
  if (!Array.isArray(files)) files = [];
  const filesHost = el("div", { class: "mt-1 space-y-0.5" });
  const paintFiles = () => {
    filesHost.replaceChildren(
      ...(files.length
        ? files.map((f) => {
            const name = cleanText(f.atsName) || cleanText(f.originalName) || `file-${f.id}`;
            return el("div", { class: "flex items-center gap-2 text-[11px]" }, [
              el("a", {
                href: apiUrl(`/api/surveillances/records/files/${f.id}`),
                target: "_blank",
                rel: "noopener",
                class: "text-dae hover:underline truncate max-w-[28rem]",
                text: name,
                title: name,
              }),
              el("button", {
                class: "text-sm font-semibold text-gray-400 hover:text-red-600 shrink-0 px-1 leading-none",
                text: "✕",
                title: "Delete file",
                onclick: async () => {
                  if (!confirm(`Delete ${name}?`)) return;
                  try {
                    await api(`/api/surveillances/records/files/${f.id}`, { method: "DELETE" });
                    afterChange?.();
                  } catch (e) { alert(cleanText(e?.message)); }
                },
              }),
            ]);
          })
        : [el("div", { class: "text-[11px] text-gray-400", text: "No files yet — Upload or drop files here" })]),
    );
  };
  paintFiles();

  async function uploadPickedFiles(picked) {
    const list = [...(picked || [])].filter(Boolean);
    if (!list.length) return;
    const dates = await promptDocumentDates(list);
    if (!dates) return;
    let fail = 0;
    for (const f of list) {
      try {
        await uploadBinary(
          `/api/surveillances/aircraft/${saId}/records/${it.checklistItemId}/files`,
          f,
          {
            filename: f.name,
            mime: f.type,
            documentDate: dates.get(f),
            componentKey: it.componentKey != null ? it.componentKey : "",
          },
        );
      } catch (e) {
        fail++;
        console.warn(e);
      }
    }
    if (fail) alert(`${fail} upload(s) failed`);
    statusSel.value = "provided";
    statusSel.className = recordsStatusSelectClass("provided");
    afterChange?.();
  }

  const fileInput = el("input", { type: "file", multiple: true, class: "hidden" });
  fileInput.addEventListener("change", async () => {
    const picked = [...(fileInput.files || [])];
    fileInput.value = "";
    await uploadPickedFiles(picked);
  });

  const dropHint = el("div", {
    class: "hidden text-[11px] font-medium text-blue-600 mt-1",
    text: it.componentLabel
      ? `Drop files for ${it.componentLabel}`
      : "Drop files to upload to this checklist item",
  });

  const baseRowClass = "flex items-start gap-3 px-4 py-2 rounded-lg border border-transparent transition-colors duration-150";
  const hoverRowClass = "bg-gray-100";
  const dragRowClass = "bg-blue-50 border-blue-400 ring-2 ring-blue-200";
  const row = el("div", {
    class: baseRowClass,
    title: "Drop documents here to upload to this item",
  });

  let dragDepth = 0;
  const setIdle = () => {
    row.className = baseRowClass;
    dropHint.classList.add("hidden");
  };
  const setHover = () => {
    if (dragDepth > 0) return;
    row.className = `${baseRowClass} ${hoverRowClass}`;
  };
  const setDragOver = () => {
    row.className = `${baseRowClass} ${dragRowClass}`;
    dropHint.classList.remove("hidden");
  };

  row.addEventListener("mouseenter", setHover);
  row.addEventListener("mouseleave", () => {
    if (dragDepth === 0) setIdle();
  });
  row.addEventListener("dragenter", (e) => {
    if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
    e.preventDefault();
    dragDepth += 1;
    setDragOver();
  });
  row.addEventListener("dragover", (e) => {
    if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOver();
  });
  row.addEventListener("dragleave", (e) => {
    if (![...(e.dataTransfer?.types || [])].includes("Files") && dragDepth === 0) return;
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setIdle();
  });
  row.addEventListener("drop", async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth = 0;
    setIdle();
    const dropped = [...(e.dataTransfer?.files || [])];
    if (!dropped.length) return;
    await uploadPickedFiles(dropped);
  });

  row.append(
    el("div", { class: "flex-1 min-w-0" }, [
      el("div", {
        class: "text-sm text-gray-800",
        text: `[${it.atsNode || "Unmapped"}] ${it.label}`,
      }),
      it.atsNameExample
        ? el("div", {
            class: "text-[11px] text-gray-400 mt-0.5 font-mono truncate",
            title: it.atsNameExample,
            text: it.atsNameExample,
          })
        : null,
      it.conditional ? el("div", { class: "text-[11px] text-amber-600", text: "Conditional — only if changed since delivery" }) : null,
      filesHost,
      dropHint,
    ]),
    el("div", { class: "flex items-center gap-2 shrink-0" }, [
      el("button", {
        class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-dae/10",
        text: "Upload",
        title: "Choose files, or drag and drop onto this row",
        onclick: () => fileInput.click(),
      }),
      fileInput,
      statusSel,
    ]),
  );
  return row;
}

async function initPhysical(host, saId, onMutate) {
  try {
    const res = await api(`/api/surveillances/aircraft/${saId}/photos`);
    const folders = res.folders || [];
    const photos = res.photos || [];
    const integrity = res.integrity || {};
    const mode = res.physicalMode === "manual" ? "manual" : "folders";
    const reportPct = Math.round(Number(res.physicalReportPct) || 0);
    const pct = Math.round(Number(res.physicalPct) || 0);
    const reload = () => initPhysical(host, saId, onMutate);
    const refreshAll = () => { onMutate?.(); reload(); };

    const modeFlash = savedFlash();
    const modeSel = el("select", { class: "border rounded px-2 py-1 text-sm" }, [
      el("option", { value: "folders", text: "Track photo folders", selected: mode === "folders" }),
      el("option", { value: "manual", text: "Mark progress (no photo upload)", selected: mode === "manual" }),
    ]);
    modeSel.addEventListener("change", async () => {
      try {
        await saveAircraftPatch(saId, { physicalMode: modeSel.value });
        flashSaved(modeFlash);
        refreshAll();
      } catch (e) {
        flashSaved(modeFlash, false);
        alert(cleanText(e?.message) || "Could not update physical mode");
      }
    });

    const header = el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 mb-3 space-y-3" }, [
      el("div", { class: "flex flex-wrap items-center justify-between gap-2 text-sm" }, [
        el("span", { class: "font-semibold", text: "Physical completion" }),
        el("span", { class: "font-medium text-gray-800", text: `${pct}%` }),
      ]),
      progressBar(pct),
      el("div", { class: "flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100" }, [
        el("span", { class: "text-xs text-gray-500", text: "Tracking mode" }),
        modeSel,
        modeFlash,
      ]),
    ]);

    if (mode === "manual") {
      const reportFlash = savedFlash();
      const reportSel = progressPctSelect(reportPct);
      reportSel.addEventListener("change", async () => {
        try {
          await saveAircraftPatch(saId, { physicalReportPct: Number(reportSel.value) });
          flashSaved(reportFlash);
          refreshAll();
        } catch (e) {
          flashSaved(reportFlash, false);
          alert(cleanText(e?.message) || "Could not update physical progress");
        }
      });
      host.replaceChildren(
        header,
        el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 space-y-3" }, [
          el("div", { class: "text-sm font-medium text-gray-800", text: "Mark progress without photos" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Photo folders are not required. Set physical progress here (5% steps) — it appears on the collapsed aircraft row. Use “Physical inspection performed” above to mark the inspection done.",
          }),
          fieldFlash("Physical progress", reportSel, reportFlash),
        ]),
      );
      return;
    }

    const byKey = (folder, sub) => `${folder}||${sub || ""}`;
    const photosByFolder = new Map();
    for (const p of photos) {
      const k = byKey(p.folder, p.subfolder);
      if (!photosByFolder.has(k)) photosByFolder.set(k, []);
      photosByFolder.get(k).push(p);
    }
    const uniqueFolders = [];
    const seenFolderKeys = new Set();
    for (const f of folders) {
      const k = byKey(f.folder, f.subfolder);
      if (seenFolderKeys.has(k)) continue;
      seenFolderKeys.add(k);
      uniqueFolders.push(f);
    }

    const integrityBanner = integrity.ok === false
      ? el("div", {
          class: "mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900",
        }, [
          el("div", { class: "font-medium", text: "Photo storage mismatch" }),
          el("div", {
            class: "text-xs mt-1",
            text: [
              `${integrity.dbCount ?? photos.length} photo record(s) in the database.`,
              integrity.fileMissing ? `${integrity.fileMissing} file(s) missing on disk.` : null,
              integrity.thumbMissing ? `${integrity.thumbMissing} thumbnail(s) could not be rebuilt.` : null,
              integrity.thumbsRepaired ? `${integrity.thumbsRepaired} thumbnail(s) repaired.` : null,
              "Missing files stay listed so you can delete the orphan record or re-upload.",
            ].filter(Boolean).join(" "),
          }),
        ])
      : (integrity.thumbsRepaired
        ? el("div", {
            class: "mb-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800",
            text: `Repaired ${integrity.thumbsRepaired} missing thumbnail(s) so previously uploaded photos show in the gallery.`,
          })
        : null);

    const exportBtn = el("button", {
      class: "px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
      text: "Export to ATS (ZIP)",
      title: "This MSN only — records + photos",
      onclick: () => exportZip(saId),
    });
    const folderBlocks = uniqueFolders.map((f) => {
      const k = byKey(f.folder, f.subfolder);
      const fp = photosByFolder.get(k) || [];
      const titleBase = `${f.folder}${f.subfolder ? ` / ${f.subfolder}` : ""}`;
      const title = f.componentLabel ? `${titleBase} · ${f.componentLabel}` : titleBase;
      const missingInFolder = fp.filter((p) => p.fileMissing || p.fileExists === false).length;
      const gallery = el("div", { class: "grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2 mt-2" },
        fp.map((p, idx) => photoThumb(p, saId, refreshAll, fp, idx)));
      const progressBarInner = el("div", { class: "h-full bg-blue-500 transition-all duration-150", style: "width: 0%" });
      const progressBar = el("div", { class: "h-2 bg-gray-100 rounded-full overflow-hidden mt-2 hidden" }, [progressBarInner]);
      const progressLabel = el("div", { class: "text-xs text-gray-500 mt-1 hidden" });
      const uploadInput = el("input", {
        type: "file",
        accept: ".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif",
        multiple: true,
        class: "hidden",
      });
      uploadInput.addEventListener("change", async () => {
        const files = [...(uploadInput.files || [])];
        uploadInput.value = "";
        if (!files.length) return;
        progressBar.classList.remove("hidden");
        progressLabel.classList.remove("hidden");
        const summary = await uploadFolderPhotos(saId, f.folder, f.subfolder, files, (frac, done, total, name) => {
          const pct = Math.round(frac * 100);
          progressBarInner.style.width = `${pct}%`;
          const current = Math.min(total, done + (frac > 0 && done < total ? 1 : 0));
          progressLabel.textContent = `Uploading ${current}/${total} (${pct}%)${name ? ` — ${name}` : ""}`;
        });
        progressBar.classList.add("hidden");
        progressLabel.classList.add("hidden");
        progressBarInner.style.width = "0%";
        if (summary.fail > 0) {
          alert(`${summary.fail} upload(s) failed:\n${summary.failedNames.slice(0, 6).join("\n")}`);
        }
        refreshAll();
      });
      const complete = f.status === "complete" && fp.length > 0;
      const notApplicable = f.status === "not_applicable";
      // Stale "complete" with zero photos should not show as done.
      if (f.status === "complete" && fp.length === 0) {
        // best-effort UI correction; API reset happens on delete
      }
      return el("div", { class: "bg-white rounded-xl border border-gray-200 p-3" }, [
        el("div", { class: "flex items-center justify-between" }, [
          el("div", { class: "flex items-center gap-2 flex-wrap" }, [
            el("span", { class: "font-medium text-sm text-gray-800", text: title }),
            pill(`${fp.length} photo${fp.length === 1 ? "" : "s"}`, "bg-gray-100 text-gray-600"),
            missingInFolder ? pill(`${missingInFolder} missing file${missingInFolder === 1 ? "" : "s"}`, "bg-amber-100 text-amber-800") : null,
            complete ? pill("Complete", "bg-green-100 text-green-700") : null,
            notApplicable ? pill("N/A", "bg-gray-200 text-gray-600") : null,
          ]),
          el("div", { class: "flex gap-2 flex-wrap justify-end" }, [
            el("button", { class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-gray-50", text: "+ Photos", onclick: () => uploadInput.click() }),
            uploadInput,
            !complete && !notApplicable ? el("button", {
              class: "text-xs text-white bg-green-600 rounded px-2 py-1 hover:bg-green-700",
              text: "Mark complete + watermark",
              onclick: () => markComplete(saId, f.folder, f.subfolder, refreshAll, { bar: progressBar, label: progressLabel }),
            }) : null,
            !notApplicable ? el("button", {
              class: "text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50",
              text: "Not applicable",
              title: "This section does not exist on this aircraft — counts toward 100%",
              onclick: async () => {
                if (!confirm(`Mark "${title}" as not applicable?`)) return;
                try {
                  await api(`/api/surveillances/aircraft/${saId}/folders/status`, jsonBody("PUT", {
                    folder: f.folder, subfolder: f.subfolder || null, status: "not_applicable",
                  }));
                  refreshAll();
                } catch (e) { alert(cleanText(e?.message)); }
              },
            }) : el("button", {
              class: "text-xs text-gray-500 border border-gray-200 rounded px-2 py-1",
              text: "Reopen",
              onclick: async () => {
                try {
                  await api(`/api/surveillances/aircraft/${saId}/folders/status`, jsonBody("PUT", {
                    folder: f.folder, subfolder: f.subfolder || null, status: "open",
                  }));
                  refreshAll();
                } catch (e) { alert(cleanText(e?.message)); }
              },
            }),
          ]),
        ]),
        progressLabel,
        progressBar,
        fp.length ? gallery : el("div", { class: "text-xs text-gray-400 mt-2", text: "No photos yet." }),
      ]);
    });

    host.replaceChildren(
      header,
      el("div", { class: "flex justify-between items-center mb-3" }, [
        el("div", { class: "text-sm text-gray-600", text: `${photos.length} photos across ${uniqueFolders.length} folders` }),
        exportBtn,
      ]),
      integrityBanner,
      el("div", { class: "space-y-3" }, folderBlocks),
    );
  } catch (e) {
    host.replaceChildren(errorNode(e));
  }
}

function openPhotoViewer(photos, startIdx, saId, refresh) {
  if (!photos?.length) return;
  let idx = Math.max(0, Math.min(startIdx, photos.length - 1));

  const overlay = el("div", {
    class: "fixed inset-0 z-[80] bg-black/90 flex flex-col",
    role: "dialog",
    "aria-modal": "true",
  });
  const topBar = el("div", { class: "flex items-center justify-between px-4 py-3 text-white shrink-0" });
  const title = el("div", { class: "text-sm font-medium truncate pr-4" });
  const counter = el("div", { class: "text-xs text-white/70" });
  const closeBtn = el("button", {
    type: "button",
    class: "text-white/80 hover:text-white text-2xl leading-none px-2",
    text: "×",
    title: "Close",
  });
  topBar.appendChild(title);
  topBar.appendChild(el("div", { class: "flex items-center gap-3" }, [counter, closeBtn]));

  const img = el("img", {
    class: "max-h-[72vh] max-w-full object-contain rounded shadow-2xl bg-black select-none",
    alt: "Surveillance photo",
  });
  const prevBtn = el("button", {
    type: "button",
    class: "absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-3xl px-3 py-2",
    text: "‹",
    title: "Previous",
  });
  const nextBtn = el("button", {
    type: "button",
    class: "absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-3xl px-3 py-2",
    text: "›",
    title: "Next",
  });
  const stageWrap = el("div", { class: "relative w-full h-full flex items-center justify-center" }, [prevBtn, img, nextBtn]);
  const stage = el("div", { class: "flex-1 flex items-center justify-center px-4 pb-2 min-h-0" }, [stageWrap]);

  const toolBar = el("div", { class: "flex flex-wrap items-center justify-center gap-2 px-4 py-3 shrink-0" });
  const mkBtn = (label, titleText, onClick, extra = "") =>
    el("button", {
      type: "button",
      class: `px-3 py-1.5 text-sm rounded-lg border border-white/25 text-white hover:bg-white/10 ${extra}`,
      text: label,
      title: titleText,
      onclick: onClick,
    });

  const render = () => {
    const p = photos[idx];
    const fv = p.isWatermarked ? "w" : "o";
    img.src = apiUrl(`/api/surveillances/photos/${p.id}/file?v=${fv}-${p.id}`);
    title.textContent = p.originalName || `Photo ${p.id}`;
    counter.textContent = `${idx + 1} / ${photos.length}`;
    prevBtn.style.visibility = photos.length > 1 ? "visible" : "hidden";
    nextBtn.style.visibility = photos.length > 1 ? "visible" : "hidden";
  };

  const close = () => {
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };

  const rotatePhoto = async (degrees) => {
    const p = photos[idx];
    try {
      await api(`/api/surveillances/photos/${p.id}/rotate`, jsonBody("PUT", { degrees }));
      render();
      refresh?.();
    } catch (e) {
      alert(cleanText(e?.message) || "Rotate failed");
    }
  };

  const deletePhoto = async () => {
    const p = photos[idx];
    if (!confirm("Delete this photo?")) return;
    try {
      await api(`/api/surveillances/photos/${p.id}`, { method: "DELETE" });
      photos.splice(idx, 1);
      if (!photos.length) { close(); refresh?.(); return; }
      if (idx >= photos.length) idx = photos.length - 1;
      render();
      refresh?.();
    } catch (e) {
      alert(cleanText(e?.message) || "Delete failed");
    }
  };

  prevBtn.addEventListener("click", () => { idx = (idx - 1 + photos.length) % photos.length; render(); });
  nextBtn.addEventListener("click", () => { idx = (idx + 1) % photos.length; render(); });
  closeBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });

  toolBar.appendChild(mkBtn("↺ Left", "Rotate left", () => rotatePhoto(-90)));
  toolBar.appendChild(mkBtn("↻ Right", "Rotate right", () => rotatePhoto(90)));
  toolBar.appendChild(mkBtn("Delete", "Delete photo", deletePhoto, "border-red-400/50 text-red-200 hover:bg-red-900/30"));
  toolBar.appendChild(mkBtn("Close", "Close viewer", close));

  function onKey(e) {
    if (e.key === "Escape") close();
    if (e.key === "ArrowLeft") { idx = (idx - 1 + photos.length) % photos.length; render(); }
    if (e.key === "ArrowRight") { idx = (idx + 1) % photos.length; render(); }
  }
  document.addEventListener("keydown", onKey);

  overlay.appendChild(topBar);
  overlay.appendChild(stage);
  overlay.appendChild(toolBar);
  document.body.appendChild(overlay);
  render();
}

function photoThumb(p, saId, refresh, folderPhotos = null, photoIdx = 0) {
  const wrap = el("div", { class: "photo-thumb-tile" });
  const missing = p.fileMissing || p.fileExists === false;
  const open = () => {
    if (missing) {
      alert(`File missing on disk for “${p.originalName || `photo ${p.id}`}”. Delete this orphan record or re-upload the photo.`);
      return;
    }
    const list = (folderPhotos && folderPhotos.length ? folderPhotos : [p]).filter((x) => !(x.fileMissing || x.fileExists === false));
    if (!list.length) {
      alert("No readable photo files in this folder.");
      return;
    }
    const idx = Math.max(0, list.findIndex((x) => x.id === p.id));
    openPhotoViewer(list, idx >= 0 ? idx : 0, saId, refresh);
  };

  if (missing) {
    const placeholder = el("div", {
      class: "w-full h-20 rounded border border-amber-300 bg-amber-50 flex flex-col items-center justify-center cursor-pointer px-1",
      title: `${p.originalName || "Photo"} — file missing on disk`,
      onclick: open,
    }, [
      el("div", { class: "text-[10px] font-medium text-amber-800", text: "Missing file" }),
      el("div", { class: "text-[9px] text-amber-700 truncate w-full text-center", text: p.originalName || `#${p.id}` }),
    ]);
    wrap.appendChild(placeholder);
  } else {
    const img = el("img", {
      src: photoThumbUrl(p),
      class: "w-full h-20 object-cover rounded border border-gray-200 cursor-pointer",
      title: p.originalName,
      loading: "lazy",
    });
    img.addEventListener("click", open);
    // Keep the tile visible on load failure — fall back to full image, then a placeholder.
    img.addEventListener("error", () => {
      if (img.dataset.fallback === "1") {
        img.replaceWith(el("div", {
          class: "w-full h-20 rounded border border-amber-300 bg-amber-50 flex items-center justify-center text-[10px] text-amber-800 cursor-pointer",
          text: "Unavailable",
          title: p.originalName,
          onclick: open,
        }));
        return;
      }
      img.dataset.fallback = "1";
      img.src = apiUrl(`/api/surveillances/photos/${p.id}/file?v=o-${p.id}`);
    });
    wrap.appendChild(img);
  }

  const tools = el("div", { class: "flex justify-between mt-0.5" }, [
    el("button", { class: "text-[10px] text-gray-500 hover:text-dae", text: missing ? "Info" : "View", title: missing ? "File missing" : "Open viewer", onclick: open }),
    p.isWatermarked ? el("span", { class: "text-[10px] text-green-600", text: "WM" }) : el("span", {}),
    el("button", { class: "text-xs font-semibold text-gray-500 hover:text-red-600 px-1", text: "✕", title: "Delete", onclick: async () => { if (!confirm(missing ? "Delete orphan photo record (file already missing)?" : "Delete photo?")) return; try { await api(`/api/surveillances/photos/${p.id}`, { method: "DELETE" }); refresh(); } catch (e) { alert(cleanText(e?.message)); } } }),
  ]);
  wrap.appendChild(tools);
  return wrap;
}

async function markComplete(saId, folder, subfolder, refresh, progressEls = null) {
  if (!confirm(`Mark "${folder}${subfolder ? " / " + subfolder : ""}" complete? Photos will be watermarked and the admin notified.`)) return;
  const { bar, label } = progressEls || {};
  try {
    if (bar && label) {
      bar.classList.remove("hidden");
      label.classList.remove("hidden");
      label.textContent = "Watermarking photos…";
      bar.firstChild.style.width = "30%";
    }
    const res = await api(`/api/surveillances/aircraft/${saId}/folders/complete`, jsonBody("PUT", { folder, subfolder: subfolder || null }));
    if (bar && label) {
      bar.firstChild.style.width = "100%";
      label.textContent = "Done";
    }
    if (res?.watermarkFailed > 0) {
      alert(`Folder marked complete, but ${res.watermarkFailed} photo(s) could not be watermarked.`);
    }
    refresh();
  } catch (e) { alert(cleanText(e?.message) || "Failed"); }
  finally {
    if (bar && label) {
      bar.classList.add("hidden");
      label.classList.add("hidden");
      bar.firstChild.style.width = "0%";
    }
  }
}

async function exportZip(saId, { msn = "" } = {}) {
  try {
    const useDots = confirm(
      `ATS export for ${msn ? `MSN ${msn}` : "this aircraft"} only.\n\nFile names use dots by default:\nMSN.Node.DDMonYYYY.FileName\n\nOK = dots · Cancel = hyphens`
    );
    const separator = useDots ? "." : "-";
    const resp = await fetch(apiUrl(`/api/surveillances/aircraft/${saId}/export`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ separator }),
    });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const j = await resp.json();
        if (j?.error) detail = j.error;
      } catch { /* body may be empty */ }
      alert(`Export failed: ${detail}`);
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = el("a", { href: url, download: `ATS_MSN${msn || saId}.zip` });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { alert(cleanText(e?.message) || "Export failed"); }
}

function htmlEsc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function statusEmailColor(status) {
  const map = {
    "Not Started": "#6b7280",
    Scheduled: "#2563eb",
    "In Progress": "#d97706",
    Completed: "#15803d",
    Cancelled: "#9ca3af",
  };
  return map[status] || "#6b7280";
}

function buildWeeklySurveillanceEml(data) {
  const subject = data.subject || "WEEKLY REPORT | SURVEILLANCES";
  const op = htmlEsc(data.surveillance?.operator || "OPERATOR");
  const week = data.week || {};
  const aircraft = data.aircraft || [];
  const summary = data.summary?.totals || {};
  const perAc = data.summary?.perAircraft || [];
  const comments = data.newComments || [];
  const outstanding = data.outstanding || [];
  const last = data.lastReport?.sentAt ? fmtDate(data.lastReport.sentAt) : "first report";

  const avgR = aircraft.length
    ? Math.round(aircraft.reduce((s, a) => s + Number(a.recordsPct || 0), 0) / aircraft.length)
    : 0;
  const avgP = aircraft.length
    ? Math.round(aircraft.reduce((s, a) => s + Number(a.physicalPct || 0), 0) / aircraft.length)
    : 0;
  const avgA = aircraft.length
    ? Math.round(aircraft.reduce((s, a) => s + Number(a.airReportPct || 0), 0) / aircraft.length)
    : 0;

  const rowsHtml = aircraft.map((a) => {
    const st = displayOverallStatus(a);
    return `<tr>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${htmlEsc(a.msn)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${htmlEsc(a.registration || "—")}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:${statusEmailColor(st)};font-weight:600;">${htmlEsc(st)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${Math.round(Number(a.recordsPct) || 0)}%</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${inspectionPerformed(a) ? "Inspected" : "Not inspected"}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${Math.round(Number(a.physicalPct) || 0)}%</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${Math.round(Number(a.airReportPct) || 0)}%</td>
    </tr>`;
  }).join("");

  const changeRows = perAc.filter((p) =>
    p.fieldChanges || p.filesUploaded || p.toInProgress || p.toCompleted || p.commentsAdded
  ).map((p) => `<tr>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${htmlEsc(p.msn)}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${p.fieldChanges}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${p.filesUploaded}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${p.toInProgress}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${p.toCompleted}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${p.commentsAdded}</td>
  </tr>`).join("") || `<tr><td colspan="6" style="padding:8px;color:#6b7280;">No field/file/status changes since ${htmlEsc(last)}.</td></tr>`;

  const commentHtml = comments.length
    ? comments.map((c) =>
        `<div style="margin:6px 0;padding:8px;background:#fff7ed;border-left:3px solid #ea580c;">
          <div style="font-size:11px;color:#9a3412;"><b>MSN ${htmlEsc(c.msn)}</b> · ${htmlEsc(c.createdBy || "")} · ${htmlEsc(fmtDate(c.createdAt))}</div>
          <div style="margin-top:4px;white-space:pre-wrap;">${htmlEsc(c.body)}</div>
        </div>`
      ).join("")
    : `<div style="color:#6b7280;font-size:12px;">No new comments since ${htmlEsc(last)}.</div>`;

  const byMsn = new Map();
  for (const o of outstanding) {
    const key = o.msn || "?";
    if (!byMsn.has(key)) byMsn.set(key, []);
    byMsn.get(key).push(o);
  }
  const matrixHtml = byMsn.size
    ? [...byMsn.entries()].map(([msn, items]) =>
        `<div style="margin:10px 0;">
          <div style="font-weight:700;margin-bottom:4px;">MSN ${htmlEsc(msn)}</div>
          <ul style="margin:0;padding-left:18px;font-size:12px;">
            ${items.map((i) => `<li style="color:#b91c1c;">[${htmlEsc(i.atsNode || "?")}] ${htmlEsc(i.label)}</li>`).join("")}
          </ul>
        </div>`
      ).join("")
    : `<div style="color:#15803d;font-size:12px;">No outstanding checklist items (or fleet is on manual records tracking).</div>`;

  const html = `<!DOCTYPE html><html><body style="font-family:Segoe UI,Arial,sans-serif;color:#111827;font-size:13px;">
  <div style="border-top:4px solid #9f1239;padding-top:12px;">
    <h1 style="margin:0 0 4px;font-size:18px;color:#9f1239;">WEEKLY REPORT · SURVEILLANCES</h1>
    <div style="color:#6b7280;margin-bottom:16px;">${op} · Week ${String(week.week || "").padStart(2, "0")}-${week.year || ""} · Changes since ${htmlEsc(last)}</div>

    <h2 style="font-size:14px;margin:18px 0 8px;">Fleet snapshot</h2>
    <table style="border-collapse:collapse;width:100%;max-width:720px;margin-bottom:12px;">
      <tr>
        <td style="padding:10px;background:#f3f4f6;border-radius:8px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Aircraft</div><div style="font-size:20px;font-weight:700;">${aircraft.length}</div></td>
        <td style="width:8px;"></td>
        <td style="padding:10px;background:#eff6ff;border-radius:8px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Avg records</div><div style="font-size:20px;font-weight:700;color:#2563eb;">${avgR}%</div></td>
        <td style="width:8px;"></td>
        <td style="padding:10px;background:#ecfdf5;border-radius:8px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Avg physical</div><div style="font-size:20px;font-weight:700;color:#15803d;">${avgP}%</div></td>
        <td style="width:8px;"></td>
        <td style="padding:10px;background:#fff7ed;border-radius:8px;text-align:center;"><div style="font-size:11px;color:#6b7280;">Avg AIR</div><div style="font-size:20px;font-weight:700;color:#c2410c;">${avgA}%</div></td>
      </tr>
    </table>

    <table style="border-collapse:collapse;width:100%;font-size:12px;">
      <thead>
        <tr style="background:#f9fafb;text-align:left;">
          <th style="padding:6px 8px;">MSN</th><th style="padding:6px 8px;">Reg</th><th style="padding:6px 8px;">Status</th>
          <th style="padding:6px 8px;text-align:right;">Records</th><th style="padding:6px 8px;">Physical</th>
          <th style="padding:6px 8px;text-align:right;">Photos %</th><th style="padding:6px 8px;text-align:right;">AIR</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>

    <h2 style="font-size:14px;margin:22px 0 8px;">Changes since last report</h2>
    <div style="margin-bottom:8px;font-size:12px;color:#374151;">
      Summary — field edits: <b>${summary.fieldChanges || 0}</b> ·
      files uploaded: <b>${summary.filesUploaded || 0}</b> ·
      moved in progress: <b>${summary.toInProgress || 0}</b> ·
      moved completed: <b>${summary.toCompleted || 0}</b> ·
      comments: <b>${summary.commentsAdded || 0}</b>
    </div>
    <table style="border-collapse:collapse;width:100%;font-size:12px;">
      <thead>
        <tr style="background:#f9fafb;text-align:left;">
          <th style="padding:6px 8px;">MSN</th>
          <th style="padding:6px 8px;text-align:right;">Fields</th>
          <th style="padding:6px 8px;text-align:right;">Files</th>
          <th style="padding:6px 8px;text-align:right;">→ In prog</th>
          <th style="padding:6px 8px;text-align:right;">→ Done</th>
          <th style="padding:6px 8px;text-align:right;">Comments</th>
        </tr>
      </thead>
      <tbody>${changeRows}</tbody>
    </table>

    <h2 style="font-size:14px;margin:22px 0 8px;color:#c2410c;">New comments</h2>
    ${commentHtml}

    <h2 style="font-size:14px;margin:22px 0 8px;">Outstanding records (checklist)</h2>
    ${matrixHtml}

    <div style="margin-top:24px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">AC Tracker · Surveillances weekly report</div>
  </div>
</body></html>`;

  const eml = [
    "MIME-Version: 1.0",
    "X-Unsent: 1",
    `Subject: ${subject}`,
    "Content-Type: text/html; charset=utf-8",
    "Content-Transfer-Encoding: 8bit",
    "",
    html,
  ].join("\r\n");
  return { subject, eml, html, week };
}

async function downloadWeeklySurveillanceReport(s) {
  try {
    const data = await api(`/api/surveillances/${s.id}/weekly-report`);
    const built = buildWeeklySurveillanceEml(data);
    const blob = new Blob([built.eml], { type: "message/rfc822" });
    const url = URL.createObjectURL(blob);
    const a = el("a", {
      href: url,
      download: `ACTracker-surveillances-WK${String(built.week.week || "").padStart(2, "0")}-${built.week.year || ""}.eml`,
    });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    if (confirm("Mark this report as sent so the next weekly email only shows newer changes?")) {
      await api(`/api/surveillances/${s.id}/weekly-report/mark-sent`, jsonBody("POST", {
        snapshot: { aircraft: data.aircraft },
        summary: data.summary,
      }));
    }
  } catch (e) {
    alert(cleanText(e?.message) || "Could not build weekly report");
  }
}

// ---- Settings: Inspectors & Companies --------------------------------------

async function initSettings(host, opts = {}) {
  const embedded = Boolean(opts.embedded);
  try {
    const [insRes, coRes] = await Promise.all([api("/api/surveillances/inspectors"), api("/api/surveillances/companies")]);
    const inspectors = insRes.inspectors || [];
    const companies = coRes.companies || [];
    const reload = () => initSettings(host, opts);

    const companyOptions = (selectedId) => [
      el("option", { value: "", text: "— No company —", selected: !selectedId }),
      ...companies.map((c) => el("option", { value: String(c.id), text: c.name, selected: String(c.id) === String(selectedId) })),
    ];

    // ---- Companies ----
    const newCompany = { name: "" };
    const coForm = el("div", { class: "flex gap-2" }, [
      el("input", { placeholder: "Company name", class: "border rounded px-2 py-1 text-sm flex-1", onchange: (e) => (newCompany.name = e.target.value) }),
      el("button", { class: "text-sm text-white bg-dae rounded px-3 py-1", text: "Add company", onclick: async () => { if (!cleanText(newCompany.name)) return; try { await api("/api/surveillances/companies", jsonBody("POST", newCompany)); reload(); } catch (e) { alert(cleanText(e?.message)); } } }),
    ]);
    const coList = el("div", { class: "flex flex-wrap gap-2 mt-2" }, companies.length ? companies.map((c) =>
      el("span", { class: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700" }, [
        el("span", { text: c.name }),
        el("button", { class: "text-gray-400 hover:text-red-600", title: "Delete company", text: "×", onclick: async () => { if (!confirm(`Delete company "${c.name}"? Inspectors keep their record but lose the link.`)) return; try { await api(`/api/surveillances/companies/${c.id}`, { method: "DELETE" }); reload(); } catch (e) { alert(cleanText(e?.message)); } } }),
      ])
    ) : [el("span", { class: "text-xs text-gray-400", text: "No companies yet." })]);

    // ---- Add inspector (company first) ----
    const newInspector = { firstName: "", lastName: "", email: "", phone: "", languages: "", companyId: "" };
    const addForm = el("div", { class: "grid grid-cols-2 md:grid-cols-7 gap-2" }, [
      el("select", { class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.companyId = e.target.value) }, companyOptions("")),
      el("input", { placeholder: "First name", class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.firstName = e.target.value) }),
      el("input", { placeholder: "Last name", class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.lastName = e.target.value) }),
      el("input", { placeholder: "Email", class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.email = e.target.value) }),
      el("input", { placeholder: "Phone", class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.phone = e.target.value) }),
      el("input", { placeholder: "Languages", class: "border rounded px-2 py-1 text-sm", onchange: (e) => (newInspector.languages = e.target.value) }),
      el("button", { class: "text-sm text-white bg-dae rounded px-3 py-1", text: "Add", onclick: async () => { if (!cleanText(newInspector.firstName)) { alert("First name is required"); return; } try { await api("/api/surveillances/inspectors", jsonBody("POST", { ...newInspector, companyId: newInspector.companyId || null })); reload(); } catch (e) { alert(cleanText(e?.message)); } } }),
    ]);

    const tbody = el("tbody", {});
    for (const i of inspectors) tbody.appendChild(inspectorRow(i, companyOptions, reload));
    const insTable = el("table", { class: "w-full text-sm" }, [
      el("thead", { class: "bg-gray-50 text-gray-600" }, [el("tr", {}, ["Company", "Name", "Email", "Phone", "Languages", "Files", ""].map((h) => el("th", { class: "text-left font-medium px-3 py-2", text: h })))]),
      tbody,
    ]);

    const cards = [
      el("div", { class: "bg-white rounded-xl border border-gray-200 p-4 mb-4" }, [
        el("div", { class: "text-sm font-semibold text-gray-800 mb-2", text: "Companies" }), coForm, coList,
      ]),
      el("div", { class: "bg-white rounded-xl border border-gray-200 p-4" }, [
        el("div", { class: "text-sm font-semibold text-gray-800 mb-2", text: "Inspectors" }), addForm,
        el("div", { class: "mt-3 border border-gray-100 rounded-lg overflow-hidden" }, [insTable]),
      ]),
    ];

    if (embedded) host.replaceChildren(...cards);
    else host.replaceChildren(
      pageShell("Inspectors & Companies", "Surveillance settings", [
        el("button", { class: "px-3 py-2 text-sm border border-gray-200 rounded-lg", text: "← Dashboard", onclick: () => go("#/surveillances") }),
      ]),
      ...cards,
    );
  } catch (e) {
    host.replaceChildren(errorNode(e));
  }
}

// One inspector: a view row, an inline edit mode, and an expandable files manager.
// Returns a fragment (main row + hidden files row) appended into the table body.
function inspectorRow(i, companyOptions, reload) {
  const frag = document.createDocumentFragment();
  const filesCell = el("td", { class: "p-0", colspan: "7" });
  const filesRow = el("tr", { class: "hidden bg-gray-50 border-t border-gray-100" }, [filesCell]);
  let filesLoaded = false;

  const toggleFiles = async () => {
    const willOpen = filesRow.classList.contains("hidden");
    filesRow.classList.toggle("hidden");
    if (willOpen && !filesLoaded) { filesLoaded = true; await loadInspectorFiles(i, filesCell); }
  };

  const mainRow = el("tr", { class: "border-t border-gray-100" }, [
    el("td", { class: "px-3 py-2 text-gray-600", text: cleanText(i.companyName) || "—" }),
    el("td", { class: "px-3 py-2 font-medium", text: inspectorName(i) }),
    el("td", { class: "px-3 py-2", text: cleanText(i.email) || "—" }),
    el("td", { class: "px-3 py-2", text: cleanText(i.phone) || "—" }),
    el("td", { class: "px-3 py-2", text: cleanText(i.languages) || "—" }),
    el("td", { class: "px-3 py-2" }, [el("button", { class: "text-xs text-dae underline", text: `📎 ${i.fileCount || 0}`, onclick: toggleFiles })]),
    el("td", { class: "px-3 py-2 text-right whitespace-nowrap" }, [
      el("button", { class: "text-xs text-dae mr-2", text: "Edit", onclick: () => startEdit() }),
      el("button", { class: "text-xs text-red-600", text: "Delete", onclick: async () => { if (!confirm("Delete inspector?")) return; try { await api(`/api/surveillances/inspectors/${i.id}`, { method: "DELETE" }); reload(); } catch (e) { alert(cleanText(e?.message)); } } }),
    ]),
  ]);

  function startEdit() {
    const draft = { firstName: i.firstName || "", lastName: i.lastName || "", email: i.email || "", phone: i.phone || "", languages: i.languages || "", companyId: i.companyId || "" };
    const mk = (key, ph) => el("input", { value: draft[key] || "", placeholder: ph, class: "border rounded px-2 py-1 text-sm w-full", onchange: (e) => (draft[key] = e.target.value) });
    const coSel = el("select", { class: "border rounded px-2 py-1 text-sm w-full", onchange: (e) => (draft.companyId = e.target.value) }, companyOptions(draft.companyId));
    const editRow = el("tr", { class: "border-t border-gray-100 bg-amber-50" }, [
      el("td", { class: "px-3 py-2" }, [coSel]),
      el("td", { class: "px-3 py-2" }, [el("div", { class: "flex gap-1" }, [mk("firstName", "First"), mk("lastName", "Last")])]),
      el("td", { class: "px-3 py-2" }, [mk("email", "Email")]),
      el("td", { class: "px-3 py-2" }, [mk("phone", "Phone")]),
      el("td", { class: "px-3 py-2" }, [mk("languages", "Languages")]),
      el("td", { class: "px-3 py-2" }),
      el("td", { class: "px-3 py-2 text-right whitespace-nowrap" }, [
        el("button", { class: "text-xs text-white bg-dae rounded px-2 py-1 mr-1", text: "Save", onclick: async () => { if (!cleanText(draft.firstName)) { alert("First name is required"); return; } try { await api(`/api/surveillances/inspectors/${i.id}`, jsonBody("PUT", { ...draft, companyId: draft.companyId || null })); reload(); } catch (e) { alert(cleanText(e?.message)); } } }),
        el("button", { class: "text-xs text-gray-500", text: "Cancel", onclick: () => editRow.replaceWith(mainRow) }),
      ]),
    ]);
    mainRow.replaceWith(editRow);
  }

  frag.appendChild(mainRow);
  frag.appendChild(filesRow);
  return frag;
}

async function loadInspectorFiles(i, cell) {
  cell.replaceChildren(el("div", { class: "p-3" }, [loadingNode()]));
  try {
    const res = await api(`/api/surveillances/inspectors/${i.id}/files`);
    const files = res.files || [];
    const fileInput = el("input", { type: "file", multiple: true, class: "hidden" });
    fileInput.addEventListener("change", async () => {
      const list = [...(fileInput.files || [])];
      for (const f of list) {
        try { await uploadBinary(`/api/surveillances/inspectors/${i.id}/files`, f, { filename: f.name, mime: f.type }); }
        catch (e) { alert(`${f.name}: ${cleanText(e?.message)}`); }
      }
      await loadInspectorFiles(i, cell);
    });
    const items = files.length ? files.map((f) => el("div", { class: "flex items-center gap-2 text-xs py-0.5" }, [
      el("a", { href: apiUrl(`/api/surveillances/inspector-files/${f.id}`), target: "_blank", class: "text-dae underline", text: cleanText(f.originalName) || `file ${f.id}` }),
      el("span", { class: "text-gray-400", text: fmtBytes(f.sizeBytes) }),
      el("button", { class: "text-red-600 hover:underline", text: "Delete", onclick: async () => { if (!confirm("Delete file?")) return; try { await api(`/api/surveillances/inspector-files/${f.id}`, { method: "DELETE" }); await loadInspectorFiles(i, cell); } catch (e) { alert(cleanText(e?.message)); } } }),
    ])) : [el("div", { class: "text-xs text-gray-400", text: "No files yet." })];
    cell.replaceChildren(el("div", { class: "p-3 space-y-1" }, [
      el("div", { class: "flex items-center justify-between mb-1" }, [
        el("div", { class: "text-xs font-semibold text-gray-600", text: `Reference files — ${inspectorName(i)}` }),
        el("button", { class: "text-xs text-dae border border-gray-200 rounded px-2 py-1 hover:bg-white", text: "+ Upload file(s)", onclick: () => fileInput.click() }),
      ]),
      fileInput,
      ...items,
    ]));
  } catch (e) {
    cell.replaceChildren(el("div", { class: "p-3" }, [errorNode(e)]));
  }
}

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
